// ============================================================
// DriveGroupContent.jsx
// EV Powertrain Simulator — 주행 전략 탭 콘텐츠
// ControlModal에서 분리된 그룹 컴포넌트
// ============================================================
import React from 'react';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { PI, PF, ModalSectionTitle } from '../shared/helpers.jsx';
import { mono } from '../shared/theme.js';
import { AWDGroundForceViz } from '../shared/advanced_widgets.jsx';
import { useColLayout } from '../../contexts/ColLayoutContext.js';

export function DriveGroupContent({ params, up, subTab, setSubTab, group, setGroup, COLORS, lang, e,
  getModePIGains, upModeGain, resetModeToDefault, resetAllModesToDefault,
  selLutMode, setSelLutMode, selAwdMode, setSelAwdMode,
  selRegenMode, setSelRegenMode, regenView, setRegenView,
  piEditMode, setPiEditMode,
  hideY2_ctrl_kw, set_hideY2_ctrl_kw,
}) {
  const colLayout = useColLayout();
  const _gc = colLayout === 1 ? '1fr' : colLayout === 3 ? '1fr 1fr 1fr' : colLayout === 4 ? '1fr 1fr 1fr 1fr' : '1fr 1fr';
  return (
    <div>
          {subTab === "sig_input" && (() => {
            // ── [A17] APP 데드존 미리보기 차트 데이터 ─────────────────────
            const dz = Math.min(0.45, (params.app_deadzone_pct ?? 2) / 100);
            const rocLimitPctS = params.app_roc_limit ?? 100;
            const previewData = Array.from({length: 21}, (_, i) => {
              const raw = i / 20;
              const eff = raw <= dz ? 0 : Math.min(1, (raw - dz) / Math.max(0.01, 1 - dz));
              return { raw: +(raw * 100).toFixed(1), eff: +(eff * 100).toFixed(1) };
            });
            return (
              <div>
                <ModalSectionTitle icon="🧑‍✈️"
                  title={e("운전자 입력 / 신호 처리 — Driver Input & Signal Processing","Driver Input & Signal Processing")}
                  color="#64748b" />

                {/* 신호 흐름 개요 */}
                <div style={{ marginBottom:'14px', padding:"10px 14px", borderRadius:'8px',
                  background:"#64748b12", border:"1px solid #64748b30",
                  fontSize:'10px', color:COLORS.textDim, fontFamily:mono, lineHeight:1.9 }}>
                  <div style={{ fontWeight:700, color:"#94a3b8", marginBottom:'4px' }}>
                    📐 {e("신호 처리 흐름 (시뮬 연동)","Signal Processing Flow (Sim-connected)")}
                  </div>
                  <span style={{color:"#94a3b8"}}>APP (0~100%)</span>
                  {" → "}<span style={{color:'#f59e0b',fontWeight:700}}>데드존 필터</span>{" → ROC 제한 → "}
                  <span style={{color:"#22c55e",fontWeight:700}}>driveModeThrottle()</span>
                  <span style={{fontSize:'9px',color:"#4ade80",marginLeft:'4px'}}>✅ 시뮬 반영</span><br/>
                  <span style={{color:"#94a3b8"}}>BPP (0~100%)</span>
                  {" → "}<span style={{color:'#f59e0b',fontWeight:700}}>감지 임계값</span>{" → 회생/유압 중재 → "}
                  <span style={{color:"#f97316",fontWeight:700}}>Arbitration</span>
                  <span style={{fontSize:'9px',color:"#4ade80",marginLeft:'4px'}}>{e("✅ 시뮬 반영","✅ Sim Applied")}</span><br/>
                  <span style={{color:"#94a3b8"}}>{e("WLTP 드라이버","WLTP Driver")}</span>
                  {" → "}<span style={{color:'#f59e0b',fontWeight:700}}>PI + look-ahead ff</span>{" → "}
                  <span style={{color:"#22c55e",fontWeight:700}}>simWLTP() / simDrivingCycle()</span>
                  <span style={{fontSize:'9px',color:"#4ade80",marginLeft:'4px'}}>✅ 시뮬 반영</span>
                </div>

                {/* [A18] 2열 배치: 좌-APP 가속 페달, 우-BPP+원페달+WLTP PI */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:'12px', alignItems:"start" }}>
                  {/* 좌: APP 페달 설정 + 미리보기 차트 */}
                  <div style={{ padding:"10px 14px", borderRadius:'8px',
                    background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:"#94a3b8", marginBottom:'8px' }}>
                      🦶 {e("APP — 가속 페달 신호 처리","APP — Accelerator Pedal Processing")}
                    </div>
                    <PI label={e("APP 데드존","APP Deadzone")}
                      value={params.app_deadzone_pct ?? 2}
                      onChange={v => up("app_deadzone_pct", v)}
                      unit="%" step={0.5} min={0} max={10}
                      desc={e("페달 무반응 영역 → driveModeThrottle() 반영","Dead band → driveModeThrottle()")} />
                    <PI label={e("최대 변화율 (ROC)","Max Rate of Change (ROC)")}
                      value={params.app_roc_limit ?? 100}
                      onChange={v => up("app_roc_limit", v)}
                      unit="%/s" step={5} min={10} max={500}
                      desc={e("초당 최대 페달 변화율 → WLTP demand ROC 클램프","Max pedal rate → WLTP demand ROC clamp")} />
                    {/* APP 데드존 미리보기 차트 */}
                    <div style={{ marginTop:'10px' }}>
                      <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'3px' }}>
                        📊 {e(`APP 데드존 미리보기 (dz=${params.app_deadzone_pct??2}%)`,`APP Deadzone Preview (dz=${params.app_deadzone_pct??2}%)`)}
                      </div>
                      <ResponsiveContainer width="100%" height={110}>
                        <LineChart data={previewData} margin={{top:4,right:8,bottom:4,left:8}}>
                          <XAxis dataKey="raw" tick={{fontSize:'8px'}} tickCount={6}
                            label={{value:e("원시 APP(%)","Raw APP(%)"),position:"insideBottom",fontSize:'8px',dy:6}} />
                          <YAxis tick={{fontSize:'8px'}} domain={[0,100]}
                            label={{value:e("유효(%)","Eff(%)"),angle:-90,position:"insideLeft",fontSize:'8px'}} />
                          <CartesianGrid strokeDasharray="2 2" stroke={COLORS.border} />
                          <Tooltip formatter={(v,n)=>[`${v}%`,n]} contentStyle={{fontSize:'9px',background:COLORS.panel,border:`1px solid ${COLORS.border}`}} />
                          <Line dataKey="raw" stroke="#64748b" strokeDasharray="3 3" dot={false} name={e("이상 직선 (1:1) (−)","이상 직선 (1:1) (−)")} strokeWidth={1}/>
                          <Line dataKey="eff" stroke="#f59e0b" dot={false} name={e("데드존 적용 (−)","데드존 적용 (−)")} strokeWidth={2}/>
                          <ReferenceLine x={params.app_deadzone_pct??2} stroke="#ef4444" strokeDasharray="3 3"
                            label={{value:"dz",fontSize:'8px',fill:"#ef4444"}} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 우: BPP + 원페달 + WLTP PI 통합 */}
                  <div style={{ display:"flex", flexDirection:"column", gap:'10px' }}>
                    {/* BPP 브레이크 페달 */}
                    <div style={{ padding:"10px 14px", borderRadius:'8px',
                      background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:"#94a3b8", marginBottom:'6px' }}>
                        🛑 {e("BPP — 브레이크 페달 신호 처리","BPP — Brake Pedal Processing")}
                        <span style={{ marginLeft:'6px', fontSize:'8px', color:"#4ade80" }}>✅ {e("시뮬 반영","Sim")}</span>
                      </div>
                      <PI label={e("BPP 데드존","BPP Deadzone")}
                        value={params.bpp_deadzone_pct ?? 3}
                        onChange={v => up("bpp_deadzone_pct", v)}
                        unit="%" step={0.5} min={0} max={15}
                        desc={e("브레이크 무반응 영역 — 회생 오신호 방지","Brake dead band")} />
                      <PI label={e("회생 전환 임계값","Brake Detect Threshold")}
                        value={params.brake_detect_threshold ?? 5}
                        onChange={v => up("brake_detect_threshold", v)}
                        unit="%" step={1} min={1} max={20}
                        desc={e("회생/유압 전환 시작 BPP — max(dz, thr)","BPP level to start regen blend")} />
                    </div>
                    {/* WLTP/FTP 드라이버 PI + 원페달 */}
                    <div style={{ padding:"10px 14px", borderRadius:'8px',
                      background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:COLORS.accent2, marginBottom:'4px' }}>
                        🏁 {e("WLTP/FTP 드라이버 속도 추종 PI","WLTP/FTP Speed-Tracking PI")}
                        <span style={{ marginLeft:'6px', fontSize:'8px', color:"#4ade80" }}>✅ {e("시뮬 반영","Sim")}</span>
                      </div>
                      <div style={{ fontSize:8.5, color:COLORS.textMuted, fontFamily:mono, marginBottom:'6px', lineHeight:1.5 }}>
                        throttle = clip(Kp·Δv + Ki·∫Δv + <b style={{color:'#f59e0b'}}>ls·Δv_ahead</b>, 0,1)
                      </div>
                      <PI label={e("드라이버 Kp","Driver Kp")}
                        value={params.wltp_tracking_kp ?? 3.0} onChange={v => up("wltp_tracking_kp", v)}
                        unit="" step={0.05} min={0.05} max={5.0}
                        desc={e("속도 오차 비례 게인","Speed error P-gain")} />
                      <PI label={e("드라이버 Ki","Driver Ki")}
                        value={params.wltp_tracking_ki ?? 0.1} onChange={v => up("wltp_tracking_ki", v)}
                        unit="" step={0.01} min={0} max={2.0}
                        desc={e("누적 오차 적분 게인","Integral gain")} />
                      <PI label={e("예견 시간 (look-ahead)","Look-Ahead")}
                        value={params.wltp_lookahead_s ?? 1.0} onChange={v => up("wltp_lookahead_s", v)}
                        unit="s" step={0.1} min={0} max={5.0}
                        desc={e("전방 속도 예견 피드포워드","Feedforward preview → simWLTP")} />
                      {/* 원페달 드라이빙 */}
                      <div style={{ marginTop:'8px', paddingTop:'8px', borderTop:`1px dashed ${COLORS.border}` }}>
                        <div style={{ fontSize:'9px', fontWeight:700, color:"#94a3b8", marginBottom:'5px' }}>
                          🦶 {e("원페달 드라이빙","One-Pedal Driving")}
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:'10px' }}>
                          <button onClick={() => up("one_pedal_enable", !params.one_pedal_enable)}
                            style={{ padding:"4px 14px", borderRadius:'5px', cursor:"pointer", fontWeight:700, fontSize:'10px',
                              background: params.one_pedal_enable ? "#22c55e" : COLORS.bg,
                              color: params.one_pedal_enable ? "#000" : COLORS.textDim,
                              border:`1px solid ${params.one_pedal_enable ? "#22c55e" : COLORS.border}` }}>
                            {params.one_pedal_enable ? "ON ✓" : "OFF"}
                          </button>
                          <span style={{ fontSize:'9px', color:COLORS.textMuted }}>
                            {e("페달 해제 시 회생 자동 적용","Regen on pedal lift")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              {/* ── [Step5] MPC 드라이버 — receding horizon 속도 추종 ── */}
              <div style={{ marginTop: '14px', background: COLORS.panel, borderRadius: '8px',
                padding: "12px 14px", border: `2px solid ${params.driver_mpc_enable ? "#818cf8" : COLORS.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: params.driver_mpc_enable ? "#818cf8" : COLORS.textDim }}>
                    🔮 {e("MPC 드라이버 — Receding Horizon","MPC Driver — Receding Horizon")}
                  </span>
                  <span style={{ fontSize: '8px', color: COLORS.textMuted }}>[Step5]</span>
                  <span style={{ marginLeft: "auto", fontSize: 8.5, color: COLORS.textMuted }}>
                    {e("우선순위: MPC > PID > throttle 고정","Priority: MPC > PID > fixed throttle")}
                  </span>
                  <button onClick={() => up("driver_mpc_enable", !params.driver_mpc_enable)}
                    style={{ padding: "3px 14px", borderRadius: '4px', cursor: "pointer", fontWeight: 700, fontSize: '10px',
                      background: params.driver_mpc_enable ? "#818cf8" : COLORS.bg,
                      color: params.driver_mpc_enable ? "#000" : COLORS.textDim,
                      border: `1px solid ${params.driver_mpc_enable ? "#818cf8" : COLORS.border}` }}>
                    {params.driver_mpc_enable ? "ON ✓" : "OFF"}
                  </button>
                </div>
                <div style={{ fontSize: 8.5, color: COLORS.textMuted, fontFamily: mono, marginBottom: '8px', lineHeight: 1.6,
                  padding: "6px 8px", borderRadius: '4px', background: COLORS.bg }}>
                  📐 min J = Σ<sub>k=0</sub><sup>N-1</sup> [q·(v_ref−v[k])² + r·(ΔF[k])²]<br/>
                  📐 v[k+1] = v[k] + dt/m·(F[k] − F_res(v[k])) &nbsp;|&nbsp; |ΔF| ≤ dF_max, |F| ≤ F_max<br/>
                  📐 {e("최적화: gradient descent 10회 반복 (중앙차분 수치 미분)","Optimization: 10-iter gradient descent (central diff.)")}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: GRID.auto180, gap: "0 24px" }}>
                  <PI label={e("예측 지평 N","Prediction Horizon N")}
                    value={params.mpc_N ?? 10} onChange={v => up("mpc_N", Math.round(v))}
                    unit="steps" step={1} min={3} max={30}
                    desc={e("N×dt 초 앞까지 예측. 길수록 부드럽고 느림","Predict N·dt s ahead. Larger = smoother but slower")} />
                  <PI label={e("속도 오차 가중치 q","Velocity Error Weight q")}
                    value={params.mpc_q_pos ?? 100} onChange={v => up("mpc_q_pos", v)}
                    unit="" step={10} min={1} max={1000}
                    desc={e("q↑ → 속도 추종 강화, 부드러움↓","q↑ → tighter tracking, less smooth")} />
                  <PI label={e("입력 변화율 가중치 r","Input Rate Weight r")}
                    value={params.mpc_r_u ?? 1} onChange={v => up("mpc_r_u", v)}
                    unit="" step={0.1} min={0.01} max={50}
                    desc={e("r↑ → ΔF 억제, 부드럽지만 응답 느림","r↑ → suppresses ΔF, smoother but slower response")} />
                  <PI label={e("ΔF 변화량 제한","ΔF Rate Limit")}
                    value={params.mpc_dU_max ?? 50} onChange={v => up("mpc_dU_max", v)}
                    unit="Nm" step={5} min={5} max={500}
                    desc={e("스텝당 허용 토크 변화 상한 [Nm]. 구동력 변환 후 적용.","Max torque change per step [Nm]. Converted to force internally.")} />
                </div>
                {params.driver_mpc_enable && params.driver_pid_enable !== false && (
                  <div style={{ marginTop: '8px', padding: "5px 8px", borderRadius: '4px', background: "#818cf810", fontSize: '9px', color: "#818cf8", fontFamily: mono }}>
                    ✓ {e("MPC 활성 — PID(Kp_drv/Ki_drv)는 비활성화됨. WLTP/FTP PI는 별도 시뮬에서 계속 사용됩니다.","MPC active — PID (Kp_drv/Ki_drv) bypassed. WLTP/FTP PI still used in its own sim.")}
                  </div>
                )}
              </div>
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════
              [A16] 드라이브 모드 총괄 탭 (drv_mode)
              신호 처리 → 드라이브 모드 선택 블럭에 대응
          ══════════════════════════════════════════════════════════ */}
          {subTab === "drv_mode" && (() => {
            const MODES = [
              { id:"ECO",      icon:"🌿", color:"#22c55e",     label:"ECO",
                desc:e("연비 최우선 · 낮은 토크 응답 · 강한 회생","Fuel efficiency · Low torque response · Strong regen"),
                sensKey:"eco_pedal_sensitivity",    ratioKey:"eco_max_torque_ratio",
                tauKey:"tau_torque_eco",             slewKey:"torque_slew_rate_eco",
                sDef:0.6, rDef:0.7, tauDef:0.08, slewDef:500 },
              { id:"NORMAL",   icon:"⚖️", color:COLORS.accent,  label:"NORMAL",
                desc:e("균형 잡힌 응답 · 표준 회생","Balanced response · Standard regen"),
                sensKey:"normal_pedal_sensitivity", ratioKey:"normal_max_torque_ratio",
                tauKey:"tau_torque_normal",          slewKey:"torque_slew_rate_normal",
                sDef:0.85, rDef:1.0, tauDef:0.04, slewDef:1500 },
              { id:"SPORT",    icon:"🏎️", color:"#ef4444",      label:"SPORT",
                desc:e("즉각적 응답 · 최대 토크 · 약한 회생","Immediate response · Max torque · Weak regen"),
                sensKey:"sport_pedal_sensitivity",  ratioKey:"sport_max_torque_ratio",
                tauKey:"tau_torque_sport",           slewKey:"torque_slew_rate_sport",
                sDef:1.0, rDef:1.15, tauDef:0.02, slewDef:3000 },
              { id:"SNOW",     icon:"❄️", color:"#38bdf8",      label:"SNOW",
                desc:e("낮은 토크 · 슬립 방지 · 완만한 응답","Low torque · Anti-slip · Gentle response"),
                sensKey:"snow_pedal_sensitivity",   ratioKey:"snow_max_torque_ratio",
                tauKey:"tau_torque_snow",            slewKey:"torque_slew_rate_snow",
                sDef:0.4, rDef:0.55, tauDef:0.12, slewDef:300 },
              { id:"OFF_ROAD", icon:"🏔️", color:"#a855f7",      label:"OFF-ROAD",
                desc:e("험지 최적화 · AWD 우선 · 안정 토크","Off-road · AWD priority · Stable torque"),
                sensKey:"offroad_pedal_sensitivity",ratioKey:"offroad_max_torque_ratio",
                tauKey:"tau_torque_offroad",         slewKey:"torque_slew_rate_offroad",
                sDef:0.75, rDef:1.0, tauDef:0.06, slewDef:800 },
            ];
            // [A16-fix] IIFE 내부 useState 불가 → params.drive_mode 직접 바인딩
            const selMode = params.drive_mode || "NORMAL";
            const setSelMode = (v) => up("drive_mode", v);
            const m = MODES.find(x => x.id === selMode) || MODES[1];
            return (
              <div>
                <ModalSectionTitle icon="🎛️"
                  title={e("드라이브 모드 총괄 설정 — Drive Mode Configuration","Drive Mode — Configuration")}
                  color="#22c55e" />

                {/* 모드 선택 */}
                <div style={{ display:"flex", gap:'4px', marginBottom:'14px', flexWrap:"wrap" }}>
                  {MODES.map(md => (
                    <button key={md.id} onClick={() => setSelMode(md.id)}
                      style={{ flex:1, minWidth:70, padding:"8px 6px", borderRadius:'6px',
                        cursor:"pointer", textAlign:"center",
                        background: selMode===md.id ? md.color : "transparent",
                        color: selMode===md.id ? "#000" : COLORS.textDim,
                        border:`2px solid ${selMode===md.id ? md.color : COLORS.border}`,
                        fontWeight:700, fontSize:'10px', transition:"all 0.15s" }}>
                      <div style={{fontSize:'16px',marginBottom:'2px'}}>{md.icon}</div>
                      {md.label}
                    </button>
                  ))}
                </div>

                {/* 선택 모드 설명 */}
                <div style={{ marginBottom:'14px', padding:"8px 12px", borderRadius:'6px',
                  background:`${m.color}12`, border:`1px solid ${m.color}40`,
                  fontSize:'10px', color:m.color, fontFamily:mono }}>
                  {m.icon} <strong>{m.label}</strong> — {m.desc}
                </div>

                {/* 선택 모드 파라미터 */}
                <div style={{ padding:"10px 14px", borderRadius:'8px',
                  background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize:'10px', fontWeight:700, color:m.color, marginBottom:'10px' }}>
                    ⚙️ {e(`${m.label} 모드 — 토크 응답 설정`,`${m.label} Mode — Torque Response`)}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                    <PI label={e("페달 응답 감도","Pedal Sensitivity")}
                      value={params[m.sensKey] ?? m.sDef}
                      onChange={v => up(m.sensKey, v)}
                      unit="" step={0.05} min={0.1} max={1.0}
                      desc={e("1.0 = 선형, >1 = 초기 민감, <1 = 완만","1.0=linear, >1=sensitive, <1=gentle")} />
                    <PI label={e("최대 토크 비율","Max Torque Ratio")}
                      value={params[m.ratioKey] ?? m.rDef}
                      onChange={v => up(m.ratioKey, v)}
                      unit="" step={0.05} min={0.2} max={1.3}
                      desc={e("T_max_motor 대비 비율 (1.0 = 100%)","Ratio vs T_max_motor (1.0 = 100%)")} />
                    <PI label={e("토크 응답 시정수 τ","Torque Response τ")}
                      value={params[m.tauKey] ?? m.tauDef}
                      onChange={v => up(m.tauKey, v)}
                      unit="s" step={0.005} min={0.005} max={0.3}
                      desc={e("1차 지연 τ (작을수록 즉각적)","1st-order lag τ (smaller = faster)")} />
                    <PI label={e("토크 슬루율","Torque Slew Rate")}
                      value={params[m.slewKey] ?? m.slewDef}
                      onChange={v => up(m.slewKey, v)}
                      unit="Nm/s" step={50} min={50} max={5000}
                      desc={e("초당 최대 토크 변화율","Max torque change rate per second")} />
                  </div>
                </div>

                {/* [D-1] 회생 강도 슬라이더 — 드라이브 모드 탭에 통합 */}
                {(() => {
                  const REGEN_MODES = [
                    {id:"ECO",      key:"eco_regen_strength",     def:0.5, c:"#22c55e"},
                    {id:"NORMAL",   key:"normal_regen_strength",  def:0.7, c:COLORS.accent},
                    {id:"SPORT",    key:"sport_regen_strength",   def:1.0, c:"#ef4444"},
                    {id:"SNOW",     key:"snow_regen_strength",    def:0.3, c:"#38bdf8"},
                    {id:"OFF_ROAD", key:"offroad_regen_strength", def:0.6, c:"#a855f7"},
                  ];
                  const cur = REGEN_MODES.find(r=>r.id===selMode) || REGEN_MODES[1];
                  return (
                    <div style={{ marginTop:'14px', padding:"10px 14px", borderRadius:'8px',
                      background:`${m.color}06`, border:`1px solid ${m.color}30` }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:m.color, marginBottom:'8px' }}>
                        ♻️ {e(`${m.label} 모드 — 회생 강도`,`${m.label} Mode — Regen Strength`)}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'8px', flexWrap:"wrap" }}>
                        <span style={{ fontSize:'9px', color:COLORS.textMuted }}>
                          {e("one_pedal 코스팅·제동 회생에 적용. 1.0 = 최대, 0 = 회생 없음","Applied to one-pedal coast & brake regen. 1.0=max, 0=off")}
                        </span>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:GRID.auto2, gap:"0 24px" }}>
                        <PI label={e("회생 강도","Regen Strength")}
                          value={params[cur.key]??cur.def}
                          onChange={v=>up(cur.key,v)}
                          unit="" step={0.05} min={0} max={1.0}
                          desc={e("0=회생 없음, 1.0=최대 회생. driveModeRegenStrength()에 반영","0=no regen, 1.0=max. Applies to driveModeRegenStrength()")} />
                      </div>
                      {/* 5개 모드 회생 강도 한눈에 비교 */}
                      <div style={{ marginTop:'8px', display:"flex", gap:'4px', flexWrap:"wrap" }}>
                        {REGEN_MODES.map(r=>(
                          <div key={r.id} style={{ fontSize:'8px', padding:"2px 7px", borderRadius:'5px',
                            background:r.id===selMode?`${r.c}25`:`${r.c}10`,
                            border:`1px solid ${r.id===selMode?r.c:r.c+"30"}`,
                            color:r.id===selMode?r.c:COLORS.textMuted,
                            fontWeight:r.id===selMode?700:400 }}>
                            {r.id}: {(params[r.key]??r.def).toFixed(2)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* 세부 탭 바로가기 */}
                <div style={{ marginTop:'12px', padding:"7px 12px", borderRadius:'6px',
                  background:COLORS.bg, border:`1px solid ${COLORS.border}`,
                  fontSize:'9px', color:COLORS.textMuted, display:"flex", gap:'10px', flexWrap:"wrap" }}>
                  <span>💡 {e("세부 설정","More settings")}:</span>
                  {[{label:"📊 트랙션 맵", tab:"traction_lut", c:'#f59e0b'},
                    {label:"⚖️ Arbitration", tab:"arbitration",  c:"#f97316"},
                    {label:"🛑 제동 분배", tab:"friction_brake",c:"#ef4444"},
                  ].map(b=>(
                    <button key={b.tab} onClick={()=>setSubTab(b.tab)}
                      style={{color:b.c,background:"transparent",border:"none",
                        cursor:"pointer",fontSize:'9px',padding:"0 2px",textDecoration:"underline"}}>
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {subTab === "traction_lut" && (() => { // IIFE_TRACTION_START
            const V_PTS   = [0,20,40,60,80,100,120,140];
            const APP_PTS = [0,10,25,50,75,100];
            const MODE_LIST = [
              { id:"ECO",    color:"#22c55e", lutKey:"traction_lut_eco" },
              { id:"NORMAL", color:COLORS.accent, lutKey:"traction_lut_normal" },
              { id:"SPORT",  color:"#ef4444", lutKey:"traction_lut_sport" },
              { id:"SNOW",   color:"#38bdf8", lutKey:"traction_lut_snow" },
              { id:"OFF_ROAD", color:"#a855f7", lutKey:"traction_lut_offroad" },
            ];
            const selDef = MODE_LIST.find(m => m.id === selLutMode) || MODE_LIST[1];
            const lut = (params[selDef.lutKey] || []).map(r => [...r]);
            const setCell = (ri, ci, val) => {
              const next = lut.map(r => [...r]);
              next[ri][ci] = Math.max(0, Math.min(1.3, parseFloat(val) || 0));
              up(selDef.lutKey, next);
            };
            // 그래프 데이터: APP별 V→토크 라인
            const chartData = V_PTS.map((v, ci) => {
              const pt = { v };
              APP_PTS.forEach((app, ri) => { pt[`APP${app}`] = (lut[ri] && lut[ri][ci] != null) ? +lut[ri][ci].toFixed(3) : 0; });
              return pt;
            });
            const lineColors = COLORS.isDark
              ? ["#94a3b8","#60a5fa","#4ade80","#fb923c","#c084fc","#facc15"]
              : ["#64748b","#1d4ed8","#15803d","#c2410c","#6d28d9","#a16207"];
            return (
              <div>
                <ModalSectionTitle icon="📊" title={e("트랙션 토크 맵 LUT (2D: APP% × 차속 → 목표 토크 (N·m))","Traction Torque Map LUT (2D: APP% × Speed → Target Torque (N·m))")} color="#f59e0b" />
                {/* [D-5] 탭 역할 안내 배너 */}
                <div style={{ marginBottom:'10px', padding:"7px 12px", borderRadius:'6px', fontSize:'9px',
                  background:"#f59e0b08", border:"1px solid #f59e0b30",
                  display:"flex", gap:'10px', flexWrap:"wrap", alignItems:"center" }}>
                  <span style={{ fontWeight:700, color:'#f59e0b' }}>📊 {e("트랙션 토크 맵 역할","Traction Map Role")}</span>
                  <span style={{ color:COLORS.textMuted, fontSize:8.5 }}>
                    {e("traction_lut_enable=ON: 드라이브 모드 탭의 power-function 대신 이 2D LUT 사용 → APP%×속도 → 목표 토크(N·m). OFF 시 drv_mode 탭 설정이 우선.",
                       "traction_lut_enable=ON: replaces drv_mode power-function with this 2D LUT → APP%×Speed → Target Torque(N·m). OFF = drv_mode tab takes priority.")}
                  </span>
                </div>
                {/* LUT 활성화 토글 */}
                <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'12px', padding:"8px 12px", background:"#f59e0b10", borderRadius:'6px', border:"1px solid #f59e0b30" }}>
                  <span style={{ fontSize:'11px', color:COLORS.textDim, flex:1 }}>
                    {e("LUT 활성화 시 Power-function 대신 2D 보간값 사용 (아래 표 직접 편집 가능)","Enable LUT: uses bilinear interpolation instead of power-function curve")}
                  </span>
                  <button onClick={() => up("traction_lut_enable", !params.traction_lut_enable)}
                    style={{ padding:"5px 18px", borderRadius:'5px', cursor:"pointer", fontWeight:700, fontSize:'11px',
                      background: params.traction_lut_enable ? "#f59e0b" : COLORS.bg,
                      color: params.traction_lut_enable ? "#000" : COLORS.textDim,
                      border:`1px solid ${params.traction_lut_enable ? "#f59e0b" : COLORS.border}` }}>
                    {params.traction_lut_enable ? "LUT ON" : "LUT OFF"}
                  </button>
                </div>
                {/* 모드 선택 */}
                <div style={{ display:"flex", gap:'4px', marginBottom:'10px' }}>
                  {MODE_LIST.map(m => (
                    <button key={m.id} onClick={() => setSelLutMode(m.id)}
                      style={{ flex:1, padding:"6px 4px", borderRadius:'5px', cursor:"pointer", fontSize:'10px', fontWeight:700,
                        background: selLutMode===m.id ? m.color : "transparent",
                        color: selLutMode===m.id ? "#000" : COLORS.textDim,
                        border:`1px solid ${selLutMode===m.id ? m.color : COLORS.border}` }}>
                      {m.id}
                    </button>
                  ))}
                </div>
                {/* [A18] 2열 배치: 좌-LUT 테이블, 우-차속별 목표 토크 곡선 */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:'12px', alignItems:"start", marginBottom:'12px' }}>
                  {/* 좌: 2D 테이블 편집기 + 페달 응답 */}
                  <div>
                    <div style={{ fontSize:'10px', fontWeight:700, color:selDef.color, marginBottom:'6px' }}>
                      📋 {e("룩업 테이블 (APP% × 차속 → 목표 토크 N·m)","LUT (APP% × Speed → Target Torque N·m)")}
                    </div>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ borderCollapse:"collapse", fontSize:'9px', fontFamily:mono, width:"100%" }}>
                        <thead>
                          <tr>
                            <th style={{ padding:"3px 5px", background:`${selDef.color}22`, color:selDef.color, border:`1px solid ${COLORS.border}`, whiteSpace:"nowrap", fontSize:'8px' }}>
                              {e("APP↓ V→","APP↓ V→")}
                            </th>
                            {V_PTS.map(v => (
                              <th key={v} style={{ padding:"3px 4px", background:`${selDef.color}15`, color:selDef.color, border:`1px solid ${COLORS.border}`, textAlign:"center", fontSize:'8px' }}>
                                {v}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {APP_PTS.map((app, ri) => (
                            <tr key={app}>
                              <td style={{ padding:"2px 5px", background:`${selDef.color}10`, color:selDef.color, border:`1px solid ${COLORS.border}`, fontWeight:700, whiteSpace:"nowrap", fontSize:'8px' }}>
                                {app}%
                              </td>
                              {V_PTS.map((v, ci) => {
                                const val = lut[ri] && lut[ri][ci] != null ? lut[ri][ci] : 0;
                                const heat = Math.min(1, val / 1.25);
                                const bg = `rgba(${Math.round(heat*220)},${Math.round((1-heat)*180)},${Math.round(50+heat*20)},0.18)`;
                                return (
                                  <td key={v} style={{ padding:"1px 2px", border:`1px solid ${COLORS.border}`, background:bg, textAlign:"center" }}>
                                    <input
                                      type="number" step="0.01" min="0" max="1.3"
                                      value={val.toFixed(2)}
                                      onChange={ev => setCell(ri, ci, ev.target.value)}
                                      style={{ width:38, textAlign:"center", background:"transparent", border:"none", color:COLORS.text, fontSize:'9px', fontFamily:mono, outline:"none" }}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* 리셋 버튼 */}
                    <div style={{ marginTop:'6px', display:"flex", gap:'8px', alignItems:"center" }}>
                      <button onClick={() => {
                        const maxR = {ECO:0.7,NORMAL:1.0,SPORT:1.15,SNOW:0.55,OFF_ROAD:1.0}[selDef.id]||1.0;
                        const preset = APP_PTS.map((app,ri) => V_PTS.map((v,ci) => {
                          const base = (app/100) * (params.T_max_motor||400) * maxR;
                          const vFade = Math.max(0, 1 - v/160*0.3);
                          return +Math.min(params.T_max_motor*1.3||520, base * vFade).toFixed(1);
                        }));
                        up(selDef.lutKey, preset);
                      }}
                        style={{ padding:"4px 12px", borderRadius:'5px', fontSize:'9px', cursor:"pointer", fontWeight:700,
                          background:`${selDef.color}20`, color:selDef.color, border:`1px solid ${selDef.color}50` }}>
                        🔄 {selLutMode} {e("초기화","Reset")}
                      </button>
                    </div>
                    {/* [A19] 페달 응답 · 토크 응답성 — LUT 테이블 아래 배치 */}
                    {(() => {
                      const modeResponses = {
                        ECO:      { color:"#22c55e", sensKey:"eco_pedal_sensitivity",    ratioKey:"eco_max_torque_ratio",    tauKey:"tau_torque_eco",      slewKey:"torque_slew_rate_eco",      sDef:0.6,  rDef:0.7,  sMin:0.3, sMax:1.0, rMin:0.4, rMax:1.0, tauDef:0.08, slewDef:500 },
                        NORMAL:   { color:COLORS.accent, sensKey:"normal_pedal_sensitivity", ratioKey:"normal_max_torque_ratio", tauKey:"tau_torque_normal",   slewKey:"torque_slew_rate_normal",   sDef:0.85, rDef:1.0,  sMin:0.5, sMax:1.0, rMin:0.7, rMax:1.1, tauDef:0.04, slewDef:1500 },
                        SPORT:    { color:"#ef4444", sensKey:"sport_pedal_sensitivity",  ratioKey:"sport_max_torque_ratio",  tauKey:"tau_torque_sport",    slewKey:"torque_slew_rate_sport",    sDef:1.0,  rDef:1.15, sMin:0.7, sMax:1.0, rMin:0.9, rMax:1.3, tauDef:0.02, slewDef:3000 },
                        SNOW:     { color:"#38bdf8", sensKey:"snow_pedal_sensitivity",   ratioKey:"snow_max_torque_ratio",   tauKey:"tau_torque_snow",     slewKey:"torque_slew_rate_snow",     sDef:0.4,  rDef:0.55, sMin:0.2, sMax:0.7, rMin:0.3, rMax:0.8, tauDef:0.12, slewDef:300 },
                        OFF_ROAD: { color:"#a855f7", sensKey:"offroad_pedal_sensitivity",ratioKey:"offroad_max_torque_ratio",tauKey:"tau_torque_offroad",  slewKey:"torque_slew_rate_offroad",  sDef:0.75, rDef:1.0,  sMin:0.5, sMax:1.0, rMin:0.7, rMax:1.1, tauDef:0.06, slewDef:800 },
                      };
                      const mr = modeResponses[selLutMode] || modeResponses.NORMAL;
                      const { color, sensKey, ratioKey, tauKey, slewKey, sDef, rDef, sMin, sMax, rMin, rMax, tauDef, slewDef } = mr;
                      return (
                        <div style={{ marginTop:'10px', padding:"10px 12px", borderRadius:'8px',
                          background: params.traction_lut_enable ? "#64748b0a" : `${color}06`,
                          border: `1px solid ${params.traction_lut_enable ? COLORS.border : color+"30"}` }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:'8px' }}>
                            <div style={{ fontSize:'10px', fontWeight:700, color: params.traction_lut_enable ? COLORS.textMuted : color }}>
                              🎛️ {selLutMode} — {e("페달 응답 · 토크 응답성","Pedal & Torque Response")}
                            </div>
                            {params.traction_lut_enable && (
                              <span style={{ fontSize:'8px', color:'#f59e0b', fontFamily:mono }}>
                                ⚠️ {e("LUT OFF 시만 유효","LUT OFF only")}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'8px', fontFamily:mono }}>
                            📐 T_eff = pedal^(1/sens) × T_max × ratio  |  τ: LPF  |  Slew [N·m/s]
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
                            <div style={{ opacity: params.traction_lut_enable ? 0.4 : 1, pointerEvents: params.traction_lut_enable ? "none" : "auto" }}>
                              <PI label={e("페달 감도","Pedal Sensitivity")} value={params[sensKey]??sDef}
                                onChange={v=>up(sensKey,v)} unit="" step={0.05} min={sMin} max={sMax} />
                            </div>
                            <div style={{ opacity: params.traction_lut_enable ? 0.4 : 1, pointerEvents: params.traction_lut_enable ? "none" : "auto" }}>
                              <PI label={e("최대 토크 배율","Max Torque Ratio")} value={params[ratioKey]??rDef}
                                onChange={v=>up(ratioKey,v)} unit="×" step={0.05} min={rMin} max={rMax} />
                            </div>
                            <PI label={e("토크 응답 τ","Torque τ")} value={params[tauKey]??tauDef}
                              onChange={v=>up(tauKey,v)} unit="s" step={0.01} min={0.005} max={0.20} />
                            <PI label={e("Slew Rate","Slew Rate")} value={params[slewKey]??slewDef}
                              onChange={v=>up(slewKey,v)} unit="N·m/s" step={100} min={100} max={5000} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {/* 우: 차속별 목표 토크 곡선 */}
                  <div style={{ background:COLORS.bg, borderRadius:'8px', padding:'10px', border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:selDef.color, marginBottom:'6px' }}>
                      📈 {e(`${selLutMode} — 차속별 APP% → 토크 (N·m)`,`${selLutMode} — Torque (N·m) vs Speed`)}
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData} margin={{ top:4, right:10, bottom:4, left:0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                        <XAxis dataKey="v" tick={{ fontSize:'8px' }} label={{ value:"km/h", position:"insideBottomRight", fontSize:'8px', dx:4 }} />
                        <YAxis domain={[0, (params.T_max_motor||400)*1.2]} tick={{ fontSize:'8px' }} label={{ value:"N·m", angle:-90, position:"insideLeft", fontSize:'8px' }} />
                        <Tooltip formatter={(v,n) => [`${v} N·m`, n]} labelFormatter={v=>`${v} km/h`} contentStyle={{ fontSize:'9px' }} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                        {APP_PTS.map((app,ri) => (
                          <Line key={app} type="monotone" dataKey={`APP${app}`} name={`APP${app} (%)`}
                            stroke={lineColors[ri]} strokeWidth={ri===0?1:ri===3?2:1.5} dot={false}
                            strokeDasharray={app===0?"4 4":undefined} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* ── [A05] LUT 유효성 검사 패널 ── */}
                {(() => {
                  const vr = validateTractionLUT(lut, params.T_max_motor);
                  const pkFromLUT = getLUTPkScale(params, selDef.id);
                  if (!params.traction_lut_enable) return null;
                  return (
                    <div style={{ marginTop:'10px', padding:"10px 14px", borderRadius:'8px',
                      background: vr.errors.length ? "#ef444412" : vr.warnings.length ? "#f59e0b12" : "#22c55e12",
                      border: `1px solid ${vr.errors.length ? "#ef4444" : vr.warnings.length ? "#f59e0b" : "#22c55e"}50` }}>
                      <div style={{ fontSize:'11px', fontWeight:700, marginBottom:'6px',
                        color: vr.errors.length ? "#ef4444" : vr.warnings.length ? "#f59e0b" : "#22c55e" }}>
                        {vr.errors.length ? e("❌ LUT 오류","❌ LUT Error") : vr.warnings.length ? e("⚠️ LUT 경고","⚠️ LUT Warning") : e("✅ LUT 유효","✅ LUT Valid")}
                        <span style={{ fontSize:'10px', fontWeight:400, color:COLORS.textMuted, marginLeft:'10px' }}>
                          pkScale = {pkFromLUT.toFixed(3)} × T_max ({(pkFromLUT*(params.T_max_motor||400)).toFixed(0)} N·m)
                          {" "}→ {e("성능 그래프에 자동 반영","auto-applied to performance graphs")}
                        </span>
                      </div>
                      {vr.errors.map((msg,i) => (
                        <div key={i} style={{ fontSize:'10px', color:"#ef4444", marginBottom:'2px' }}>• {msg}</div>
                      ))}
                      {vr.warnings.map((msg,i) => (
                        <div key={i} style={{ fontSize:'10px', color:'#f59e0b', marginBottom:'2px' }}>• {msg}</div>
                      ))}
                      {vr.errors.length > 0 && (
                        <button onClick={() => {
                          const maxR = {ECO:0.7,NORMAL:1.0,SPORT:1.15,SNOW:0.55,OFF_ROAD:1.0}[selDef.id]||1.0;
                          const APP_PTS_r=[0,10,25,50,75,100], V_PTS_r=[0,20,40,60,80,100,120,140];
                          const preset = APP_PTS_r.map((app,ri) => V_PTS_r.map((v,ci) => {
                            const base = (app/100)*(params.T_max_motor||400)*maxR;
                            return +Math.max(0, base*(1-v/160*0.3)).toFixed(1);
                          }));
                          up(selDef.lutKey, preset);
                        }} style={{ marginTop:'6px', padding:"4px 14px", borderRadius:'4px', fontSize:'10px',
                          background:"#ef444420", color:"#ef4444", border:"1px solid #ef444460",
                          cursor:"pointer", fontWeight:700 }}>
                          🔄 {e("기본값으로 재설정","Reset to Default")}
                        </button>
                      )}
                    </div>
                  );
                })()}

              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════
              ② 회생 캘리브레이션 (Coasting / Braking / One-Pedal LUT)
          ══════════════════════════════════════════════════════════ */}
          {subTab === "awd_calib" && (() => {
            // ── [D-4] AWD 캘리브 탭 역할 정리 ──────────────────────────
            // 이 탭: AWD/TV LUT 캘리브레이션 + 회생 맵 + 중재 한계 + 디레이팅 통합
            // 각 섹션은 해당 전용 탭(arbitration, friction_brake)과 연결됨
            // ── [Advance03] 모드별 AWD LUT 에디터 ─────────────────────────
            const awdVPts  = params.awd_v_pts    || [0,20,40,60,80,100,120,140];
            const awdTqPts = params.awd_torq_pts || [0,20,40,60,80,100];
            const lineColors = COLORS.isDark
              ? ["#94a3b8","#60a5fa","#4ade80","#fb923c","#c084fc","#facc15"]
              : ["#64748b","#1d4ed8","#15803d","#c2410c","#6d28d9","#a16207"];

            // 모드 정의
            const AWD_MODES = [
              { id:"ECO",      icon:"🍃", color:"#22c55e",
                lutKey:"awd_front_lut_eco",    tractKey:"traction_lut_eco",
                tvKey:"tv_yaw_gain_eco",        escKey:"tv_threshold_eco",
                tvDef:600,  escDef:0.08,
                desc:  e("0:100~20:80 — 후륜 중심, 효율 우선, 앞모터 최소",
                          "0:100~20:80 — RWD-bias, efficiency, min front motor"),
                phys:  e("F=μN 후륜 집중 → 구동저항↓, 앞모터 I²R 손실 제거",
                          "F=μN rear-biased → reduces drag, eliminates front I²R loss") },
              { id:"NORMAL",   icon:"⚡", color:COLORS.accent,
                lutKey:"awd_front_lut_normal",  tractKey:"traction_lut_normal",
                tvKey:"tv_yaw_gain_normal",      escKey:"tv_threshold_normal",
                tvDef:800,  escDef:0.05,
                desc:  e("40:60~50:50 — 균형, yaw 안정, 일상 주행",
                          "40:60~50:50 — balanced, yaw stability, daily driving"),
                phys:  e("yaw rate 균형 → 언더/오버스티어 방지, 횡력 균등 분산",
                          "Balanced yaw rate → prevents under/oversteer, even lateral force") },
              { id:"SPORT",    icon:"🔥", color:"#ef4444",
                lutKey:"awd_front_lut_sport",   tractKey:"traction_lut_sport",
                tvKey:"tv_yaw_gain_sport",       escKey:"tv_threshold_sport",
                tvDef:1200, escDef:0.03,
                desc:  e("20:80~30:70 — 후륜 중심, 코너링·가속 강조",
                          "20:80~30:70 — RWD-bias, cornering & acceleration"),
                phys:  e("M=F×r 후륜 모멘트 → 민첩성↑, 제어된 오버스티어",
                          "Rear moment M=F×r → agility↑, controlled oversteer") },
              { id:"SNOW",     icon:"❄️", color:"#38bdf8",
                lutKey:"awd_front_lut_snow",    tractKey:"traction_lut_snow",
                tvKey:"tv_yaw_gain_snow",        escKey:"tv_threshold_snow",
                tvDef:400,  escDef:0.10,
                desc:  e("55:45~60:40 — 앞륜 중심, 직진성, 스핀 방지",
                          "55:45~60:40 — FWD-bias, directional stability, anti-spin"),
                phys:  e("μ 낮은 노면 언더스티어 유도 → pull 방향 직진, ABS 연동",
                          "Low-μ understeer induction → straight pull, ABS sync") },
              { id:"OFF_ROAD", icon:"🗻", color:"#a855f7",
                lutKey:"awd_front_lut_offroad",  tractKey:"traction_lut_offroad",
                tvKey:"tv_yaw_gain_offroad",      escKey:"tv_threshold_offroad",
                tvDef:600,  escDef:0.07,
                desc:  e("50:50 + 저속 동적 벡터링 — 크롤링, 험로",
                          "50:50 + low-speed dynamic vectoring — crawling & rough terrain"),
                phys:  e("크로스 액슬 접지 → 가상 락커로 4바퀴 트랙션 극대화",
                          "Cross-axle contact → virtual locker maximizes 4-wheel traction") },
            ];

            const selDef   = AWD_MODES.find(m => m.id === selAwdMode) || AWD_MODES[1];
            const awdLut   = (params[selDef.lutKey] || []).map(r => [...r]);
            const tractLut = params[selDef.tractKey] || [];
            const T_max    = params.T_max_motor || 400;
            const modeOv   = params.awd_mode_override || {};

            // 셀 업데이트
            const setCell = (ri, ci, val) => {
              const next = awdLut.map(r => [...r]);
              if (!next[ri]) next[ri] = [];
              next[ri][ci] = Math.max(0, Math.min(1, parseFloat(val) || 0));
              up(selDef.lutKey, next);
            };

            // 프리셋 복원 데이터
            const PRESETS = {
              ECO:     [[0.00,0.00,0.00,0.00,0.00,0.00,0.00,0.00],[0.10,0.08,0.05,0.05,0.05,0.05,0.05,0.05],[0.15,0.12,0.10,0.08,0.07,0.06,0.05,0.05],[0.20,0.18,0.15,0.12,0.10,0.08,0.07,0.06],[0.20,0.20,0.18,0.15,0.12,0.10,0.08,0.07],[0.20,0.20,0.20,0.18,0.15,0.12,0.10,0.08]],
              NORMAL:  [[0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],[0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],[0.50,0.46,0.43,0.42,0.41,0.40,0.40,0.40],[0.50,0.47,0.44,0.43,0.42,0.41,0.40,0.40],[0.50,0.48,0.46,0.44,0.43,0.42,0.41,0.40],[0.50,0.50,0.49,0.48,0.47,0.45,0.43,0.42]],
              SPORT:   [[0.30,0.28,0.25,0.22,0.20,0.20,0.20,0.20],[0.28,0.25,0.23,0.21,0.20,0.20,0.20,0.20],[0.26,0.24,0.22,0.21,0.20,0.20,0.20,0.20],[0.25,0.23,0.22,0.21,0.20,0.20,0.20,0.20],[0.24,0.23,0.22,0.21,0.20,0.20,0.20,0.20],[0.22,0.22,0.21,0.21,0.20,0.20,0.20,0.20]],
              SNOW:    [[0.55,0.55,0.55,0.55,0.55,0.55,0.55,0.55],[0.58,0.57,0.56,0.55,0.55,0.55,0.55,0.55],[0.60,0.59,0.58,0.57,0.56,0.55,0.55,0.55],[0.60,0.60,0.59,0.58,0.57,0.56,0.55,0.55],[0.60,0.60,0.60,0.59,0.58,0.57,0.56,0.55],[0.60,0.60,0.60,0.60,0.59,0.58,0.57,0.56]],
              OFF_ROAD:[[0.50,0.50,0.50,0.50,0.50,0.50,0.50,0.50],[0.50,0.50,0.50,0.48,0.47,0.46,0.45,0.45],[0.50,0.50,0.48,0.46,0.45,0.44,0.43,0.42],[0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.41],[0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.40],[0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.40]],
            };

            // 비교 차트 데이터 (토크 50% 행 기준)
            const midRow = Math.floor(awdTqPts.length / 2);
            const cmpData = awdVPts.map((v, ci) => {
              const pt = { v };
              AWD_MODES.forEach(m => {
                const lut = params[m.lutKey] || [];
                pt[m.id] = (lut[midRow] && lut[midRow][ci] != null) ? +lut[midRow][ci].toFixed(3) : 0.5;
              });
              return pt;
            });

            // 선택 모드 차트 데이터 (토크% 라인별) — 오버라이드 반영
            const curOv = (modeOv && modeOv[selAwdMode] != null) ? modeOv[selAwdMode] : null;
            const modeChartData = awdVPts.map((v, ci) => {
              const pt = { v };
              if (curOv != null) {
                // 오버라이드: 모든 토크% 라인을 고정값으로
                awdTqPts.forEach(tq => { pt[`T${tq}`] = +curOv.toFixed(3); });
                pt['_override'] = +curOv.toFixed(3);
              } else {
                awdTqPts.forEach((tq, ri) => {
                  pt[`T${tq}`] = (awdLut[ri] && awdLut[ri][ci] != null) ? +awdLut[ri][ci].toFixed(3) : 0.5;
                });
              }
              return pt;
            });

            return (
              <div>
                <ModalSectionTitle icon="🔀" title={e("AWD 분배 — 모드별 전륜 비율 LUT","AWD Distribution — Per-Mode Front Ratio LUT")} color="#a855f7" />

                {/* ── 활성화 배너 ── */}
                <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'14px',
                  padding:"10px 14px", background:"#a855f710", borderRadius:'8px', border:"1px solid #a855f730" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:"#a855f7", marginBottom:'3px' }}>
                      {e("드라이브 모드별 독립 AWD LUT — 트랙션 토크맵과 연동 편집",
                         "Per-Drive-Mode AWD LUT — edit linked with traction torque map")}
                    </div>
                    <div style={{ fontSize:'10px', color:COLORS.textMuted }}>
                      {params.awd_mode_lut_enable
                        ? e("✅ 모드별 LUT 활성 — 드라이브 모드 전환 시 AWD 맵 자동 교체",
                             "✅ Per-mode LUT active — AWD map auto-switches with drive mode")
                        : e("⬜ 모드별 LUT OFF — awd_front_lut 단일 공유 맵 사용",
                             "⬜ Per-mode LUT OFF — using single shared awd_front_lut")}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:'5px', alignItems:"flex-end" }}>
                    <button onClick={() => up("awd_mode_lut_enable", !params.awd_mode_lut_enable)}
                      style={{ padding:"6px 16px", borderRadius:'6px', cursor:"pointer", fontWeight:700, fontSize:'10px',
                        background: params.awd_mode_lut_enable ? "#a855f7" : COLORS.bg,
                        color:      params.awd_mode_lut_enable ? "#fff"    : COLORS.textDim,
                        border:`1px solid ${params.awd_mode_lut_enable ? "#a855f7" : COLORS.border}` }}>
                      {params.awd_mode_lut_enable ? e("모드별 ON","Per-Mode ON") : e("모드별 OFF","Per-Mode OFF")}
                    </button>
                    <button onClick={() => AWD_MODES.forEach(m => up(m.lutKey, PRESETS[m.id] || PRESETS.NORMAL))}
                      style={{ padding:"4px 14px", borderRadius:'5px', cursor:"pointer", fontWeight:600, fontSize:'10px',
                        background:COLORS.bg, color:COLORS.textMuted, border:`1px solid ${COLORS.border}` }}>
                      🔄 {e("전체 초기화","Reset All")}
                    </button>
                    <button onClick={() => up("awd_calib_enable", !params.awd_calib_enable)}
                      style={{ padding:"4px 14px", borderRadius:'5px', cursor:"pointer", fontWeight:600, fontSize:'10px',
                        background: params.awd_calib_enable ? "#6d28d9" : COLORS.bg,
                        color:      params.awd_calib_enable ? "#fff"    : COLORS.textDim,
                        border:`1px solid ${params.awd_calib_enable ? "#6d28d9" : COLORS.border}` }}>
                      {e(`시뮬 적용: ${params.awd_calib_enable?"ON ✓":"OFF"}`,
                         `Sim active: ${params.awd_calib_enable?"ON ✓":"OFF"}`)}
                    </button>
                  </div>
                </div>

                {/* ── 모드 탭 ── */}
                <div style={{ display:"flex", gap:'4px', marginBottom:'12px' }}>
                  {AWD_MODES.map(m => (
                    <button key={m.id} onClick={() => setSelAwdMode(m.id)}
                      style={{ flex:1, padding:"8px 4px", borderRadius:'7px', cursor:"pointer", textAlign:"center",
                        background: selAwdMode===m.id ? m.color : COLORS.bg,
                        color:      selAwdMode===m.id ? "#fff"  : COLORS.textDim,
                        border:`2px solid ${selAwdMode===m.id ? m.color : COLORS.border}`,
                        transition:"all 0.15s" }}>
                      <div style={{ fontSize:'17px' }}>{m.icon}</div>
                      <div style={{ fontSize:'10px', fontWeight:700, marginTop:'2px' }}>{m.id}</div>
                      {params.drive_mode === m.id && (
                        <div style={{ fontSize:'8px', marginTop:'1px', opacity:0.85 }}>▶ {e("현재","Active")}</div>
                      )}
                    </button>
                  ))}
                </div>

                {/* ── 선택 모드 설명 + 트랙션맵 참조 ── */}
                <div style={{ marginBottom:'12px', padding:"10px 12px", borderRadius:'7px',
                  background:`${selDef.color}10`, border:`1px solid ${selDef.color}35` }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:'8px', marginBottom:'6px' }}>
                    <span style={{ fontSize:'20px', lineHeight:1 }}>{selDef.icon}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'11px', fontWeight:700, color:selDef.color }}>{selDef.desc}</div>
                      <div style={{ fontSize:'10px', color:COLORS.textMuted, marginTop:'2px' }}>📐 {selDef.phys}</div>
                    </div>
                    {/* ── [A08] 오버라이드 + TV + ESC 인라인 — 복원 버튼 왼쪽 ── */}
                    {(() => {
                      const cur = modeOv[selAwdMode];
                      return (
                        <div style={{ display:"flex", alignItems:"center", gap:'6px', flexShrink:0,
                          padding:"5px 10px", borderRadius:'6px', flexWrap:"nowrap",
                          background: cur != null ? `${selDef.color}18` : `${COLORS.bg}cc`,
                          border:`1px solid ${cur != null ? selDef.color+"70" : COLORS.border}` }}>
                          {/* 오버라이드 셀렉터 */}
                          <select value={cur==null?"null":cur.toFixed(2)}
                            onChange={ev => {
                              const v = ev.target.value;
                              const n = { ...modeOv };
                              n[selAwdMode] = v === "null" ? null : parseFloat(v);
                              up("awd_mode_override", n);
                            }}
                            style={{ background:COLORS.panel, color: cur != null ? selDef.color : COLORS.text,
                              border:`1px solid ${cur != null ? selDef.color : COLORS.border}`,
                              borderRadius:'4px', fontSize:'9px', padding:"4px 5px",
                              fontWeight: cur != null ? 700 : 400, cursor:"pointer" }}>
                            <option value="null">🔗 {e("LUT (자동)","LUT (auto)")}</option>
                            {[0,0.1,0.2,0.3,0.4,0.5,0.6,0.7,0.8,0.9,1.0].map(v => (
                              <option key={v} value={v.toFixed(2)}>
                                {e(`전${(v*100).toFixed(0)}:후${((1-v)*100).toFixed(0)}`,
                                   `F${(v*100).toFixed(0)}:R${((1-v)*100).toFixed(0)}`)}
                              </option>
                            ))}
                          </select>
                          {/* TV 게인 */}
                          <span style={{ fontSize:'8px', color:COLORS.textMuted, whiteSpace:"nowrap" }}>🔀{e("TV","TV")}</span>
                          <PI label="" value={params[selDef.tvKey] ?? selDef.tvDef}
                            onChange={v=>up(selDef.tvKey,v)} unit="Nm/rs" step={50} min={0} max={3000} />
                          {/* ESC */}
                          <span style={{ fontSize:'8px', color:COLORS.textMuted, whiteSpace:"nowrap" }}>🛡️{e("ESC","ESC")}</span>
                          <PI label="" value={params[selDef.escKey] ?? selDef.escDef}
                            onChange={v=>up(selDef.escKey,v)} unit="rad/s" step={0.01} min={0.01} max={0.3} />
                        </div>
                      );
                    })()}
                    <button onClick={() => up(selDef.lutKey, PRESETS[selAwdMode] || PRESETS.NORMAL)}
                      style={{ padding:"4px 12px", borderRadius:'5px', fontSize:'9px', cursor:"pointer", fontWeight:700, flexShrink:0,
                        background:`${selDef.color}20`, color:selDef.color, border:`1px solid ${selDef.color}50` }}>
                      🔄 {e(`${selAwdMode} 복원`,`Reset ${selAwdMode}`)}
                    </button>
                  </div>
                  {/* 트랙션 맵 APP100% 참조 행 */}
                  <div style={{ padding:"6px 10px", borderRadius:'5px', background:COLORS.bg,
                    border:`1px solid ${COLORS.border}`, fontSize:'10px', fontFamily:mono }}>
                    <span style={{ color:'#f59e0b', fontWeight:700 }}>
                      📊 {e("트랙션 토크맵 참조","Traction Map Ref")} [{selAwdMode}] APP 100%:
                    </span>
                    <span style={{ color:COLORS.textMuted }}> {e("(아래 토크% 행의 실제 목표 토크 (N·m))","(actual target torque per row (N·m))")}</span>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:"4px 12px", marginTop:'4px' }}>
                      {awdVPts.map((v, ci) => {
                        const T = tractLut[tractLut.length-1] && tractLut[tractLut.length-1][ci] != null
                          ? tractLut[tractLut.length-1][ci] : null;
                        return (
                          <span key={v}>
                            <span style={{ color:COLORS.textDim }}>{v}km/h: </span>
                            <span style={{ color:'#f59e0b', fontWeight:700 }}>{T != null ? `${T}N·m` : "—"}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* ── [A08] 2열 레이아웃: LUT 테이블 (왼쪽) + 차속별 전륜 비율 곡선 (오른쪽) ── */}
                <div key={colLayout} style={{ display:"grid", gridTemplateColumns:_gc, gap:'16px', alignItems:"start" }}>
                  {/* 왼쪽: 2D LUT 편집 테이블 + 범례/버튼/오버라이드/TV게인 */}
                  <div style={{ minWidth:0 }}>


                {/* ── 2D LUT 편집 테이블 (오버라이드 시 비활성) ── */}
                <div style={{ opacity: curOv != null ? 0.35 : 1, pointerEvents: curOv != null ? "none" : "auto",
                  transition:"opacity 0.2s" }}>
                <div style={{ overflowX:"auto", marginBottom:'6px' }}>
                  <table style={{ borderCollapse:"collapse", fontSize:'9px', fontFamily:mono, minWidth:"max-content" }}>
                    <thead>
                      <tr>
                        <th style={{ padding:"5px 8px", background:`${selDef.color}22`, color:selDef.color,
                          border:`1px solid ${COLORS.border}`, whiteSpace:"nowrap", textAlign:"left" }}>
                          {e("토크%  ↓  / 속도 →","Torq%↓ / Spd→")}
                        </th>
                        {awdVPts.map(v => (
                          <th key={v} style={{ padding:"4px 8px", background:`${selDef.color}15`,
                            color:selDef.color, border:`1px solid ${COLORS.border}`, textAlign:"center", minWidth:52 }}>
                            {v}<span style={{fontSize:'8px'}}> km/h</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {awdTqPts.map((tq, ri) => {
                        // 이 행에 해당하는 트랙션 토크 (APP 보간: 0%→T0, 20%→T1 ... 100%→T5)
                        const tractRow = tractLut[ri] || [];
                        return (
                          <tr key={tq}>
                            <td style={{ padding:"4px 8px", background:`${selDef.color}10`, color:selDef.color,
                              border:`1px solid ${COLORS.border}`, fontWeight:700, whiteSpace:"nowrap" }}>
                              {tq}%
                              {tractRow[0] != null && (
                                <div style={{ fontSize:'8px', color:'#f59e0b', fontWeight:400, marginTop:'1px' }}>
                                  {e("≈","≈")}{tractRow[0]}N·m
                                </div>
                              )}
                            </td>
                            {awdVPts.map((v, ci) => {
                              const val  = awdLut[ri] && awdLut[ri][ci] != null ? awdLut[ri][ci] : 0.5;
                              const T_ref = tractRow[ci] != null ? tractRow[ci] : 0;
                              const tHeat = Math.min(1, T_ref / Math.max(T_max, 1)); // 트랙션 강도 0~1
                              // 전후 비율로 색상: 파랑=후륜, 초록=50:50, 빨강=전륜
                              const cr = val < 0.5 ? Math.round(val*2*180)       : 180;
                              const cg = Math.round((1-Math.abs(val-0.5)*2)*150);
                              const cb = val < 0.5 ? 180 : Math.round((1-val)*2*180);
                              const alpha = 0.15 + tHeat * 0.20; // 트랙션 강도 → 투명도
                              const bg = `rgba(${cr},${cg},${cb},${alpha.toFixed(2)})`;
                              return (
                                <td key={v} style={{ padding:"1px 2px", border:`1px solid ${COLORS.border}`,
                                  background:bg, textAlign:"center", verticalAlign:"top" }}>
                                  <input
                                    type="number" step="0.05" min="0" max="1"
                                    value={val.toFixed(2)}
                                    onChange={ev => setCell(ri, ci, ev.target.value)}
                                    style={{ width:46, textAlign:"center", background:"transparent",
                                      border:"none", color:COLORS.text, fontSize:'10px', fontFamily:mono, outline:"none" }}
                                  />
                                  {T_ref > 0 && (
                                    <div style={{ fontSize:7.5, color:'#f59e0b', lineHeight:1.1, paddingBottom:'2px' }}>
                                      {T_ref}N·m
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                </div>{/* end disable-wrap */}
                {/* ── LUT 안내 텍스트 (박스 없음, 투명 배경) ── */}
                <div style={{ marginTop:'4px', paddingLeft:'2px',
                  fontSize:'9px', color:COLORS.textMuted, fontFamily:mono, lineHeight:1.7 }}>
                  {curOv != null ? (
                    <span style={{ color:'#f59e0b', fontWeight:700 }}>
                      ⚠️ {e(`오버라이드 활성 — 전${((curOv)*100).toFixed(0)}:후${((1-curOv)*100).toFixed(0)} 고정 (LUT 무시)`,
                             `Override active — F${((curOv)*100).toFixed(0)}:R${((1-curOv)*100).toFixed(0)} fixed (LUT ignored)`)}
                    </span>
                  ) : (
                    <>
                      {e("전륜비율: 0.0=후100% ↔ 0.5=50:50 ↔ 1.0=전100%","Front ratio: 0.0=Rear100% ↔ 0.5=50:50 ↔ 1.0=Front100%")}<br/>
                      {e("행=요구토크% · 열=차속[km/h]","Row=Torque% · Col=Speed[km/h]")}&nbsp;
                      <span style={{ color:'#f59e0b' }}>
                        {e("셀 하단 소자 = 트랙션 토크맵 N·m (참조)","Cell sub = traction map N·m (ref)")}
                      </span>
                    </>
                  )}
                </div>
                {/* ── 범례 ── */}
                <div style={{ display:"flex", alignItems:"center", gap:'8px', marginBottom:'12px' }}>
                  <span style={{ display:"inline-block", width:18, height:8, borderRadius:'2px', background:"rgba(0,120,180,0.3)" }}/>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted }}>{e("후륜 제동 비율 (−)","후륜 제동 비율 (−)")}</span>
                  <span style={{ display:"inline-block", width:18, height:8, borderRadius:'2px', background:"rgba(60,150,60,0.3)" }}/>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted }}>{e("50:50","50:50")}</span>
                  <span style={{ display:"inline-block", width:18, height:8, borderRadius:'2px', background:"rgba(180,60,0,0.3)" }}/>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted }}>{e("전륜 제동 비율 (−)","전륜 제동 비율 (−)")}</span>
                  <span style={{ fontSize:'9px', color:COLORS.textMuted, marginLeft:'4px', opacity:0.7 }}>
                    | {e("투명도↑ = 트랙션 토크↑","opacity↑ = traction torque↑")}
                  </span>
                </div>

                  </div>

                  {/* 오른쪽: 차속별 전륜 비율 곡선 */}
                  <div style={{ minWidth:0 }}>
                    {/* ── 선택 모드 차속별 전륜 비율 곡선 (박스 없음) ── */}
                    <div style={{ background:"transparent", padding:"6px 0 0 0" }}>
                      <div style={{ fontSize:'11px', fontWeight:700, color:selDef.color, marginBottom:'2px' }}>
                        📈 {selAwdMode} — {e("차속별 전륜 비율 곡선","Front Ratio vs Speed")}
                      </div>
                      <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'6px' }}>
                        {curOv != null
                          ? <span style={{ color:'#f59e0b', fontWeight:700 }}>
                              ⚠️ {e(`오버라이드: 전${((curOv)*100).toFixed(0)}:후${((1-curOv)*100).toFixed(0)} 고정선`,
                                     `Override: F${((curOv)*100).toFixed(0)}:R${((1-curOv)*100).toFixed(0)} fixed`)}
                            </span>
                          : e("0=후100% / 0.5=50:50 / 1=전100% | 토크% 라인별","0=Rear100% / 0.5=50:50 / 1=Front100% | per Torque%")
                        }
                      </div>
                      <ResponsiveContainer width="100%" height={190}>
                        <LineChart data={modeChartData} margin={{ top:4, right:16, bottom:4, left:10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                          <XAxis dataKey="v" tick={{ fontSize:'9px' }} label={{ value:"km/h", position:"insideBottomRight", fontSize:'9px' }} />
                          <YAxis domain={[0,1]} tick={{ fontSize:'9px' }}
                            tickFormatter={v => `${(v*100).toFixed(0)}:${((1-v)*100).toFixed(0)}`}
                            label={{ value:e("전:후","F:R"), angle:-90, position:"insideLeft", fontSize:'10px' }} />
                          <ReferenceLine y={0.5} stroke="#94a3b8" strokeDasharray="5 3"
                            label={{ value:"50:50", fontSize:'8px', fill:COLORS.textMuted }} />
                          {curOv != null && (
                            <ReferenceLine y={curOv} stroke={selDef.color} strokeWidth={2.5}
                              label={{ value:`${e("전","F")}${((curOv)*100).toFixed(0)}%`, fontSize:'9px', fill:selDef.color, position:"insideLeft" }} />
                          )}
                          <Tooltip contentStyle={{background:COLORS.panel,border:`1px solid ${COLORS.border}`,borderRadius:'6px',fontSize:'10px',color:COLORS.text}}
                            formatter={(v,n) => [
                              `${e("전","F")}${(v*100).toFixed(0)}%:${e("후","R")}${((1-v)*100).toFixed(0)}%`, n
                            ]}
                            labelFormatter={v => `${v} km/h`} />
                          <Legend iconSize={10} wrapperStyle={{ fontSize:'9px' }} />
                          {curOv != null ? (
                            <Line type="monotone" dataKey="T0" name={e("오버라이드 고정 (%)", "오버라이드 고정 (%)")}
                              stroke={selDef.color} strokeWidth={2.5} dot={false} strokeDasharray="6 3" />
                          ) : (
                            awdTqPts.map((tq, ri) => (
                              <Line key={tq} type="monotone" dataKey={`T${tq}`} name={lang==="en"?`F/R Torque Split ${tq} (%)`:`전후 토크 분배 ${tq} (%)`}
                                stroke={lineColors[ri]} strokeWidth={ri===3?2.5:1.5} dot={false}
                                strokeDasharray={tq===0?"5 3":undefined} />
                            ))
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* ── 5모드 비교 차트 (토크 40% 기준) ── */}
                <div style={{ background:COLORS.bg, borderRadius:'8px', padding:'10px', border:`1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:"#a855f7", marginBottom:'4px' }}>
                    🔀 {e("5모드 AWD 비율 비교 — 토크 40% 기준선","All-Mode Comparison — Torque 40% baseline")}
                  </div>
                  <div style={{ fontSize:'9px', color:COLORS.textMuted, marginBottom:'6px' }}>
                    {e("실선 = 현재 선택 모드 / 점선 = 나머지","Solid = selected mode / Dashed = others")}
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={cmpData} margin={{ top:4, right:16, bottom:4, left:10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                      <XAxis dataKey="v" tick={{ fontSize:'9px' }} label={{ value:"km/h", position:"insideBottomRight", fontSize:'9px' }} />
                      <YAxis domain={[0,1]} tick={{ fontSize:'9px' }}
                        tickFormatter={v => `${(v*100).toFixed(0)}:${((1-v)*100).toFixed(0)}`}
                        label={{ value:e("전:후","F:R"), angle:-90, position:"insideLeft", fontSize:'10px' }} />
                      <ReferenceLine y={0.5} stroke="#94a3b8" strokeDasharray="5 3" />
                      <Tooltip contentStyle={{background:COLORS.panel,border:`1px solid ${COLORS.border}`,borderRadius:'6px',fontSize:'10px',color:COLORS.text}}
                        formatter={(v,n) => [
                          `${e("전","F")}${(v*100).toFixed(0)}%:${e("후","R")}${((1-v)*100).toFixed(0)}%`, n
                        ]}
                        labelFormatter={v => `${v} km/h`} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize:'9px' }} />
                      {AWD_MODES.map(m => (
                        <Line key={m.id} type="monotone" dataKey={m.id} name={m.id}
                          stroke={m.color}
                          strokeWidth={m.id===selAwdMode ? 2.5 : 1.5}
                          strokeDasharray={m.id===selAwdMode ? undefined : "4 3"}
                          dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {/* ── [V42-AWD-GRF] 4륜 지면반력도 실시간 시각화 ── */}
                {params.awd_enable && (
                  <AWDGroundForceViz params={params} />
                )}
                {!params.awd_enable && (
                  <div style={{ marginTop:'14px', padding:'10px 14px', background:`rgba(168,85,247,0.06)`,
                    borderRadius:'8px', border:'1px solid #a855f730', fontSize:'10px', color:COLORS.textDim }}>
                    🛞 {lang==="en"
                      ? "Enable AWD (awd_enable=true) to activate 4-Wheel Ground Reaction Force Visualization"
                      : "AWD 활성화 (awd_enable=true) 시 4륜 지면반력도 시각화가 표시됩니다"}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ══════════════════════════════════════════════════════════
              ④ Friction Brake 유압 분배 상세
          ══════════════════════════════════════════════════════════ */}
          {subTab === "friction_brake" && (() => {
            const fbVPts   = params.fric_brake_v_pts   || [0,30,60,100,130];
            const fbBPts   = params.fric_brake_bpp_pts || [0,10,25,50,75,100];
            const fbLut    = params.fric_regen_lut     || [];
            const fbColors = ["#38bdf8","#34d399","#f59e0b","#f87171","#c084fc"];
            const fbChartData = fbBPts.map((bpp,ri) => {
              const pt = { bpp };
              fbVPts.forEach((v,ci) => { pt[`V${v}`] = (fbLut[ri]&&fbLut[ri][ci]!=null)?+fbLut[ri][ci].toFixed(2):0.5; });
              return pt;
            });
            // EBD 미리보기
            const ebdData = [0,0.2,0.4,0.6,0.8,1.0].map(bd => {
              const h  = params.ebd_cg_height_m || 0.55;
              const wb = params.ebd_wheelbase_m  || 2.87;
              const fr = Math.min(0.85, (params.ebd_front_ratio_static||0.65) + bd*0.8 * h/wb * 0.3);
              return { bd:+(bd*100).toFixed(0), front:+fr.toFixed(3), rear:+(1-fr).toFixed(3) };
            });
            return (
              <div>
                <ModalSectionTitle icon="🛑" title={e("Friction Brake 유압 분배 캘리브레이션","Friction Brake Hydraulic Distribution Calibration")} color="#ef4444" />

                {/* [D-3] 기능 토글 행 */}
                <div style={{ display:"flex", gap:'8px', flexWrap:"wrap", marginBottom:'12px',
                  padding:"8px 12px", borderRadius:'7px',
                  background:COLORS.panel, border:`1px solid ${COLORS.border}` }}>
                  <span style={{ fontSize:9.5, fontWeight:700, color:COLORS.textMuted, alignSelf:"center" }}>
                    {e("회생 기능 토글","Regen Feature Toggles")}:
                  </span>
                  {[
                    {k:"regen_coast_enable", label:e("코스트 회생","Coast Regen"), c:"#10b981",
                     desc:e("코스팅 중 회생 제동 활성","Enable regen during coasting")},
                    {k:"regen_map_enable",   label:e("회생 맵","Regen Map"),    c:"#60a5fa",
                     desc:e("BPP 기반 회생 맵 사용","Use BPP-based regen map")},
                    {k:"regen_calib_enable", label:e("회생 LUT 캘리브","LUT Calib"), c:"#ef4444",
                     desc:e("2D 회생-유압 블렌딩 LUT 활성","Enable 2D regen-hydraulic blend LUT")},
                  ].map(t=>(
                    <div key={t.k} style={{ display:"flex", alignItems:"center", gap:'4px' }}>
                      <button onClick={()=>up(t.k,!(params[t.k]??false))}
                        title={t.desc}
                        style={{ padding:"2px 10px", borderRadius:'4px', border:"none", cursor:"pointer",
                          fontSize:'9px', fontWeight:700,
                          background:(params[t.k]??false)?t.c:COLORS.border,
                          color:(params[t.k]??false)?"#fff":COLORS.textMuted }}>
                        {(params[t.k]??false)?"ON":"OFF"}
                      </button>
                      <span style={{ fontSize:'9px', color:COLORS.textMuted }}>{t.label}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'14px', padding:"8px 12px", background:"#ef444410", borderRadius:'6px', border:"1px solid #ef444430" }}>
                  <span style={{ fontSize:'11px', color:COLORS.textDim, flex:1 }}>
                    {e("캘리브레이션 LUT 활성화: 회생-유압 블렌딩 2D LUT + EBD 전후 분배 + 응답 지연 모델","Enable: regen-hydraulic 2D blend LUT + EBD front/rear distribution + response delay model")}
                  </span>
                  <button onClick={() => up("friction_calib_enable", !params.friction_calib_enable)}
                    style={{ padding:"5px 18px", borderRadius:'5px', cursor:"pointer", fontWeight:700, fontSize:'11px',
                      background: params.friction_calib_enable ? "#ef4444" : COLORS.bg,
                      color: params.friction_calib_enable ? "#fff" : COLORS.textDim,
                      border:`1px solid ${params.friction_calib_enable ? "#ef4444" : COLORS.border}` }}>
                    {params.friction_calib_enable ? "LUT ON" : "LUT OFF"}
                  </button>

                  {/* ── 왼쪽: 회생-유압 블렌딩 2D LUT ── */}
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:"#ef4444", marginBottom:'6px' }}>
                      {e("① 회생-유압 블렌딩 2D LUT","① Regen-Hydraulic Blend 2D LUT")}
                      <div style={{ fontSize:'9px', fontWeight:400, color:COLORS.textMuted, marginTop:'2px' }}>
                        {e("BPP% × 차속 → 회생비율","BPP% × Speed → Regen Ratio")}
                      </div>
                    </div>
                    <div style={{ fontSize:'10px', color:COLORS.textMuted, marginBottom:'6px', fontFamily:mono }}>
                      {e("값=1.0: 100% 회생 / 값=0.0: 100% 유압","1.0=full regen / 0.0=full hydraulic")}
                    </div>
                    <div style={{ overflowX:"auto", marginBottom:'10px' }}>
                      <table style={{ borderCollapse:"collapse", fontSize:'10px', fontFamily:mono, width:"100%" }}>
                        <thead>
                          <tr>
                            <th style={{ padding:"4px 6px", background:"#ef444422", color:"#ef4444", border:`1px solid ${COLORS.border}`, whiteSpace:"nowrap" }}>BPP\V</th>
                            {fbVPts.map(v=><th key={v} style={{ padding:"4px 6px", border:`1px solid ${COLORS.border}`, textAlign:"center", color:COLORS.accent, whiteSpace:"nowrap" }}>{v}<span style={{fontSize:'8px'}}> kph</span></th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {fbBPts.map((bpp,ri)=>(
                            <tr key={bpp}>
                              <td style={{ padding:"4px 6px", background:"#ef444410", color:"#ef4444", border:`1px solid ${COLORS.border}`, fontWeight:700 }}>{bpp}%</td>
                              {fbVPts.map((v,ci)=>{
                                const val = fbLut[ri]&&fbLut[ri][ci]!=null ? fbLut[ri][ci] : 0.5;
                                const bg = `rgba(${Math.round((1-val)*220)},${Math.round(val*180)},${Math.round(val*80)},0.2)`;
                                return (
                                  <td key={v} style={{ padding:"2px 2px", border:`1px solid ${COLORS.border}`, background:bg, textAlign:"center" }}>
                                    <input type="number" step="0.05" min="0" max="1" value={val.toFixed(2)}
                                      onChange={ev=>{const n=fbLut.map(r=>[...r]);if(!n[ri])n[ri]=[];n[ri][ci]=Math.max(0,Math.min(1,parseFloat(ev.target.value)||0));up("fric_regen_lut",n);}}
                                      style={{ width:36, textAlign:"center", background:"transparent", border:"none", color:COLORS.text, fontSize:'10px', fontFamily:mono, outline:"none" }} />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <ResponsiveContainer width="100%" height={140}>
                      <LineChart data={fbChartData} margin={{ top:4, right:8, bottom:4, left:0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                        <XAxis dataKey="bpp" tick={{ fontSize:'9px' }} label={{ value:"BPP%", position:"insideBottomRight", fontSize:'9px' }} />
                        <YAxis domain={[0,1.05]} tick={{ fontSize:'9px' }} label={{ value:lang==="en"?"Regen Ratio (-)":"회생 비율 (-)", angle:-90, position:"insideLeft", fontSize:'8px' }} />
                        <Tooltip contentStyle={{ fontSize:'9px' }} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                        {fbVPts.map((v,ci)=>(
                          <Line key={v} type="monotone" dataKey={`V${v}`} name={`${v} (km/h)`}
                            stroke={fbColors[ci]} strokeWidth={1.5} dot={false} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* ── 오른쪽: EBD 전자 제동력 분배 ── */}
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:"#f97316", marginBottom:'8px' }}>
                      {e("② EBD (전자 제동력 분배)","② EBD — Front/Rear Load Transfer")}
                      <div style={{ fontSize:'9px', fontWeight:400, color:COLORS.textMuted, marginTop:'2px' }}>
                        {e("전후 하중 이동 모델","Dynamic Load Transfer Model")}
                      </div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'10px' }}>
                      <button onClick={()=>up("ebd_enable",!params.ebd_enable)}
                        style={{ padding:"4px 14px", borderRadius:'4px', cursor:"pointer", fontWeight:700, fontSize:'11px',
                          background:params.ebd_enable?"#f97316":COLORS.bg, color:params.ebd_enable?"#000":COLORS.textDim,
                          border:`1px solid ${params.ebd_enable?"#f97316":COLORS.border}` }}>
                        EBD {params.ebd_enable?"ON":"OFF"}
                      </button>
                      <span style={{ fontSize:'9px', color:COLORS.textMuted, lineHeight:1.4 }}>
                        {e("감속 → 전방 하중↑ → 전륜 제동 자동 증가","Decel → front load↑ → front brake auto-increase")}
                      </span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px", marginBottom:'10px' }}>
                      <PI label={e("정적 전륜 비율","Static Front")} value={params.ebd_front_ratio_static||0.65} onChange={v=>up("ebd_front_ratio_static",v)} unit="" step={0.05} min={0.4} max={0.85} />
                      <PI label={e("무게중심 높이","CG Height")} value={params.ebd_cg_height_m||0.55} onChange={v=>up("ebd_cg_height_m",v)} unit="m" step={0.01} min={0.3} max={1.2} />
                      <PI label={e("축거","Wheelbase")} value={params.ebd_wheelbase_m||2.87} onChange={v=>up("ebd_wheelbase_m",v)} unit="m" step={0.01} min={2.0} max={4.0} />
                      <PI label={e("ABS 압력 한계","ABS Limit")} value={params.abs_pressure_limit_bar||120} onChange={v=>up("abs_pressure_limit_bar",v)} unit="bar" step={5} min={60} max={200} />
                    </div>
                    <div style={{ fontSize:'10px', fontWeight:700, color:"#f97316", marginBottom:'4px' }}>
                      {e("감속도별 전후 분배 미리보기","EBD Preview: Dist. vs Decel")}
                    </div>
                    <ResponsiveContainer width="100%" height={140}>
                      <AreaChart data={ebdData} margin={{ top:4, right:8, bottom:4, left:0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                        <XAxis dataKey="bd" tick={{ fontSize:'9px' }} label={{ value:lang==="en"?"Brake Intensity %":"제동강도%", position:"insideBottomRight", fontSize:'8px' }} />
                        <YAxis domain={[0,1]} tick={{ fontSize:'9px' }} />
                        <Tooltip contentStyle={{ fontSize:'9px' }} formatter={v=>`${(v*100).toFixed(1)}%`} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                        <Area type="monotone" dataKey="front" name={e("전륜 제동 비율 (−)","전륜 제동 비율 (−)")} stroke="#f97316" fill="#f9731620" strokeWidth={2} />
                        <Area type="monotone" dataKey="rear"  name={e("후륜 제동 비율 (−)","후륜 제동 비율 (−)")}  stroke="#38bdf8" fill="#38bdf820" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* ── 응답 시간 (전폭) ── */}
                <div style={{ marginTop:'14px' }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:COLORS.textDim, marginBottom:'8px' }}>{e("③ 제동 응답 시간 파라미터","③ Brake Response Time Parameters")}</div>
                  <div style={{ display:"grid", gridTemplateColumns:GRID.auto180, gap:"0 24px" }}>
                    <PI label={e("유압 빌드업 지연","Hydraulic Rise Time")} value={params.brake_pressure_rise_ms||80} onChange={v=>up("brake_pressure_rise_ms",v)} unit="ms" step={5} min={20} max={200} />
                    <PI label={e("회생→유압 전환 시간","Regen→Friction Trans.")} value={params.regen_to_friction_ms||50} onChange={v=>up("regen_to_friction_ms",v)} unit="ms" step={5} min={10} max={150} />
                  </div>
                </div>

                {/* ── ④ Regen Brake Blending 적합 (Fix-RB-1~3) ── */}
                <div style={{ marginTop:'18px', padding:"12px 14px", borderRadius:'8px',
                  border:"1px solid #10b98150", background:"#10b98108" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:'10px', marginBottom:'10px', flexWrap:"wrap" }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:"#10b981" }}>
                      ④ {e("비선형 회생/마찰 블렌딩 적합","Nonlinear Regen/Friction Blend Calibration")}
                    </div>
                    <button onClick={()=>up("regen_blend_enable",!(params.regen_blend_enable??false))}
                      style={{ padding:"3px 14px", borderRadius:'5px', border:"none", cursor:"pointer",
                        fontSize:'10px', fontWeight:700,
                        background:(params.regen_blend_enable??false)?"#10b981":COLORS.border,
                        color:(params.regen_blend_enable??false)?"#000":COLORS.textMuted }}>
                      {(params.regen_blend_enable??false)
                        ?e("ON — S-curve 블렌딩 활성","ON — S-curve blend active")
                        :e("OFF — 블렌딩 비활성","OFF — blend disabled")}
                    </button>
                    <button onClick={()=>up("regen_optimizer_enable",!(params.regen_optimizer_enable??false))}
                      style={{ padding:"3px 14px", borderRadius:'5px', border:"none", cursor:"pointer",
                        fontSize:'10px', fontWeight:700,
                        background:(params.regen_optimizer_enable??false)?"#6366f1":COLORS.border,
                        color:(params.regen_optimizer_enable??false)?"#fff":COLORS.textMuted }}>
                      {e("최적화","Optimizer")}: {(params.regen_optimizer_enable??false)?"ON":"OFF"}
                    </button>
                  </div>

                  {/* 블렌딩 파라미터 */}
                  <div style={{ fontSize:9.5, color:COLORS.textMuted, marginBottom:'8px', lineHeight:1.7,
                    padding:"5px 8px", borderRadius:'4px', background:"#10b98108", fontFamily:"monospace" }}>
                    blend = sigmoid(speed_fade × soc_fade, k)<br/>
                    speed_fade: 0 (v&lt;v_lo) → 1 (v&gt;v_hi) · soc_fade: SOC&gt;0.9에서 0으로<br/>
                    {e("적합 순서: ① v_lo/v_hi로 저속 fade-out 구간 설정 → ② k_nonlin으로 S-커브 기울기 조정 → ③ τ_hyd/τ_regen으로 응답 지연 보정 → ④ RB5 차트에서 blend_ratio 곡선 확인",
                       "Fit order: ① v_lo/v_hi set low-speed fade zone → ② k_nonlin tune S-curve slope → ③ τ_hyd/τ_regen match response lag → ④ check blend_ratio curve in RB5 chart")}
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:GRID.auto180, gap:"0 24px" }}>
                    <PI label={e("저속 fade-out 시작 v_lo","Low-spd fade start v_lo")}
                      value={(params.regen_blend_v_lo??5.0)*3.6}
                      onChange={v=>up("regen_blend_v_lo", v/3.6)}
                      unit="km/h" step={1} min={0} max={30}
                      desc={e("이 속도 미만에서 회생 0% (순수 마찰)","Below this speed: regen=0% (pure friction)")} />
                    <PI label={e("저속 fade-out 완료 v_hi","Low-spd fade end v_hi")}
                      value={(params.regen_blend_v_hi??15.0)*3.6}
                      onChange={v=>up("regen_blend_v_hi", v/3.6)}
                      unit="km/h" step={1} min={5} max={60}
                      desc={e("이 속도 초과에서 블렌딩 완전 활성","Above this speed: full blend active")} />
                    <PI label={e("S-커브 가파름 k_nonlin","S-curve steepness k")
                      }
                      value={params.regen_blend_k_nonlin??2.5}
                      onChange={v=>up("regen_blend_k_nonlin",v)}
                      unit="" step={0.5} min={0.5} max={10}
                      desc={e("클수록 fade-out 구간에서 회생↓ 급격히 — NVH 조율","Larger=sharper regen drop in fade zone — tune for NVH")} />
                    <PI label={e("회생 Jerk 제한","Regen Jerk Limit")}
                      value={params.regen_max_jerk??3.0}
                      onChange={v=>up("regen_max_jerk",v)}
                      unit="m/s³" step={0.5} min={0.5} max={10}
                      desc={e("낮을수록 부드러운 회생 전환 (3 m/s³: ADAS 수준)","Lower=smoother blend (3=ADAS level)")} />
                    <PI label={e("유압 시상수 τ_hyd","Hydraulic τ_hyd")}
                      value={(params.tau_hyd_brake??0.20)*1000}
                      onChange={v=>up("tau_hyd_brake",v/1000)}
                      unit="ms" step={10} min={20} max={500}
                      desc={e("유압 캘리퍼 빌드업 지연. 실차 측정: 제동 요청 → 실제 압력 상승 시간","Caliper buildup lag. Measure: brake cmd → actual pressure rise")} />
                    <PI label={e("회생 시상수 τ_regen","Regen τ_regen")}
                      value={(params.tau_regen_brake??0.02)*1000}
                      onChange={v=>up("tau_regen_brake",v/1000)}
                      unit="ms" step={5} min={5} max={200}
                      desc={e("모터 회생 응답 지연. 인버터 전류 제어 BW 기반 (~20ms 일반)","Motor regen response lag. Inverter current control BW based")} />
                  </div>

                  {/* 현재 파라미터 KPI */}
                  <div style={{ marginTop:'8px', display:"flex", gap:'8px', flexWrap:"wrap" }}>
                    {[
                      { l:"v_lo", v:`${((params.regen_blend_v_lo??5)*3.6).toFixed(0)} km/h`, c:"#f87171" },
                      { l:"v_hi", v:`${((params.regen_blend_v_hi??15)*3.6).toFixed(0)} km/h`, c:"#facc15" },
                      { l:"k", v:`${params.regen_blend_k_nonlin??2.5}`, c:"#10b981" },
                      { l:"τ_hyd", v:`${((params.tau_hyd_brake??0.2)*1000).toFixed(0)}ms`, c:"#60a5fa" },
                      { l:"τ_regen", v:`${((params.tau_regen_brake??0.02)*1000).toFixed(0)}ms`, c:"#a78bfa" },
                      { l:"jerk_max", v:`${params.regen_max_jerk??3.0} m/s³`, c:"#fb923c" },
                    ].map(k=>(
                      <div key={k.l} style={{ fontSize:'9px', padding:"2px 8px", borderRadius:'6px',
                        background:`${k.c}18`, border:`1px solid ${k.c}50`, color:k.c, fontWeight:700 }}>
                        {k.l}={k.v}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            );
          })()}


          {/* ══════════════════════════════════════════════════════════
              ⑤ Arbitration 한계값 통합 패널
          ══════════════════════════════════════════════════════════ */}
          {subTab === "arbitration" && (() => {
            // SOC vs 회생 한계 미리보기
            // [A18] 회생 맵 통합 변수
            const coastPts  = params.regen_coast_v_pts    || [0,5,15,30,60,90,120,150];
            const coastStr  = params.regen_coast_str       || [0,0,0.15,0.40,0.65,0.75,0.80,0.80];
            const coastData = coastPts.map((v,i) => ({ v, str: coastStr[i] != null ? coastStr[i] : 0 }));
            const brakeVPts  = params.regen_brake_v_pts   || [0,30,60,100,130];
            const brakeBPts  = params.regen_brake_bpp_pts || [0,10,25,50,75,100];
            const brakeLut   = params.regen_brake_lut     || [];
            const brakeData  = brakeBPts.map((bpp,ri) => {
              const pt = { bpp };
              brakeVPts.forEach((v,ci) => { pt[`V${v}`] = (brakeLut[ri]&&brakeLut[ri][ci]!=null)?+brakeLut[ri][ci].toFixed(2):0; });
              return pt;
            });
            const opAppPts = params.regen_onepedal_app_pts || [0,1,2,3,4,5,6,7,8];
            const opStr    = params.regen_onepedal_str     || [1,0.9,0.75,0.55,0.35,0.15,0.05,0,0];
            const opData   = opAppPts.map((a,i) => ({ app:a, str: opStr[i] != null ? opStr[i] : 0 }));
            const brakeLColors = ["#38bdf8","#34d399","#f59e0b","#f87171","#c084fc"];
            const REGEN_MODES_ARB = [
              { id:"ECO",      color:"#22c55e",    key:"eco_regen_strength",    def:0.5, min:0.0, max:1.0, hint:e("부드러운 회생","Gentle"), resetDecel:1.5, resetFade:20, resetBlend:0.9, resetHi:0.6 },
              { id:"NORMAL",   color:COLORS.accent, key:"normal_regen_strength", def:0.7, min:0.0, max:1.0, hint:e("표준","Standard"), resetDecel:2.5, resetFade:15, resetBlend:0.8, resetHi:0.7 },
              { id:"SPORT",    color:"#ef4444",    key:"sport_regen_strength",  def:1.0, min:0.0, max:1.0, hint:e("강한 회생","Strong"), resetDecel:3.0, resetFade:10, resetBlend:0.7, resetHi:0.9 },
              { id:"SNOW",     color:"#38bdf8",    key:"snow_regen_strength",   def:0.3, min:0.0, max:0.6, hint:e("낮게","Low"), resetDecel:1.0, resetFade:25, resetBlend:0.9, resetHi:0.4 },
              { id:"OFF_ROAD", color:"#a855f7",    key:"offroad_regen_strength",def:0.6, min:0.0, max:1.0, hint:e("중간","Mid"), resetDecel:2.0, resetFade:15, resetBlend:0.8, resetHi:0.6 },
            ];
            const selRmArb = REGEN_MODES_ARB.find(m => m.id === selRegenMode) || REGEN_MODES_ARB[1];
            const curValArb = params[selRmArb.key] ?? selRmArb.def;
            const socRegenData = Array.from({length:21},(_,i)=>{
              const soc = i*0.05;
              const taper_start = params.bms_regen_soc_taper_start || 0.85;
              const cutoff = params.bms_soc_charge_cutoff || 0.98;
              let ratio = 1.0;
              if (soc > taper_start) ratio = Math.max(0, 1 - (soc-taper_start)/(cutoff-taper_start));
              if (soc >= cutoff) ratio = 0;
              return { soc:+(soc*100).toFixed(0), ratio:+ratio.toFixed(3), limit_kw:+(ratio*(params.arb_chg_limit_kw||70)).toFixed(1) };
            });
            // 온도 디레이트 미리보기
            const derateData = Array.from({length:20},(_,i)=>{
              const T = 50+i*8;
              const motor = Math.max(0, Math.min(1, 1-(T-params.T_motor_derate_start)/(params.T_motor_derate_end-params.T_motor_derate_start+1)));
              const inv   = Math.max(0, Math.min(1, 1-(T-params.T_inv_derate_start)/(params.T_inv_derate_end-params.T_inv_derate_start+1)));
              const batt  = Math.max(0, Math.min(1, 1-(T-params.T_batt_derate_start)/(params.T_batt_derate_end-params.T_batt_derate_start+1)));
              return { T, motor:+motor.toFixed(3), inv:+inv.toFixed(3), batt:+batt.toFixed(3) };
            });
            return (
              <div>
                <ModalSectionTitle icon="⚖️" title={e("중재 / 토크 지령 — Arbitration / T_req","Arbitration / T_req")} color="#f97316" />
                {/* 시뮬 연결 배너 */}
                <div style={{ marginBottom:'10px', padding:"8px 12px", borderRadius:'6px',
                  background:"#16a34a12", border:"1px solid #16a34a40",
                  fontSize:'9px', display:"flex", gap:'14px', flexWrap:"wrap", alignItems:"center" }}>
                  <span style={{ color:"#4ade80", fontWeight:700, fontSize:'10px' }}>
                    ⚖️ {e("Arbitration → 물리식 연결 (A10)","Arbitration → Physics (A10)")}
                  </span>
                  {[
                    e("방전한계 → P_dis_max_T() 클램프","Dischg → P_dis_max_T clamp"),
                    e("충전한계 → P_chg_max_T() 클램프","Chg → P_chg_max_T clamp"),
                    e("충전한계 → regenEnergyOptimizer 상한","Chg → regenEnergyOptimizer cap"),
                  ].map((s,i)=>(
                    <span key={i} style={{ color:"#4ade80" }}>✓ {s}</span>
                  ))}
                </div>

                {/* [A18] 2열 배치: 좌-BMS 충방전 한계, 우-온도 디레이트 */}
                <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)", gap:'12px', marginBottom:'12px', alignItems:"start" }}>
                  {/* 좌: BMS 충방전 한계 */}
                  <div style={{ padding:'10px', borderRadius:'8px', border:"1px solid #f9731640", background:"#f9731608" }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:"#f97316", marginBottom:'8px' }}>
                      🔋 {e("BMS 충방전 한계","BMS 충방전 한계")}
                      <span style={{ fontWeight:400, fontSize:'9px', color:COLORS.textMuted, marginLeft:'6px' }}>{e("→ 시뮬 직접 반영","→ Sim Direct")}</span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
                      <PI label={e("방전 한계 (Dischg)","Discharge Limit")} value={params.arb_dischg_limit_kw||150} onChange={v=>up("arb_dischg_limit_kw",v)} unit="kW" step={5} min={20} max={400} />
                      <PI label={e("충전 한계 (Chg/Regen)","Charge Limit")} value={params.arb_chg_limit_kw||70} onChange={v=>up("arb_chg_limit_kw",v)} unit="kW" step={5} min={10} max={200} />
                      <PI label={e("SOC 회생 감소 시작","SOC Regen Taper")} value={(params.bms_regen_soc_taper_start||0.85)*100} onChange={v=>up("bms_regen_soc_taper_start",v/100)} unit="%" step={1} min={70} max={97} />
                      <PI label={e("SOC 회생 차단","SOC Regen Cutoff")} value={(params.bms_soc_charge_cutoff||0.98)*100} onChange={v=>up("bms_soc_charge_cutoff",v/100)} unit="%" step={0.5} min={90} max={100} />
                      <PI label={e("저온 디레이트 기온","Cold Derate Temp")} value={params.bms_cold_derate_temp||5} onChange={v=>up("bms_cold_derate_temp",v)} unit="°C" step={1} min={-20} max={15} />
                      <PI label={e("저온 디레이트 계수","Cold Derate Factor")} value={params.bms_cold_derate_factor||0.5} onChange={v=>up("bms_cold_derate_factor",v)} unit="×" step={0.05} min={0.1} max={1.0} />
                    </div>
                    <div style={{ marginTop:'8px' }}>
                      <div style={{ fontSize:'9px', fontWeight:700, color:"#f97316", marginBottom:'3px' }}>{e("SOC vs 회생 한계 커브","SOC vs Regen Limit")}</div>
                      <ResponsiveContainer width="100%" height={110}>
                        <AreaChart data={socRegenData} margin={{ top:2, right:8, bottom:2, left:0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                          <XAxis dataKey="soc" tick={{ fontSize:'8px' }} label={{ value:"SOC%", position:"insideBottomRight", fontSize:'8px' }} />
                          <YAxis yAxisId="r" domain={[0,1]} tick={{ fontSize:'8px' }} label={{ value:lang==="en"?"Ratio (-)":"비율 (-)", angle:-90, position:"insideLeft", fontSize:'8px' }} />
                          <YAxis yAxisId="kw" orientation="right" hide={hideY2_ctrl_kw} tick={{ fontSize:'8px' }} label={{ value:"kW", angle:90, position:"insideRight", fontSize:'8px' }} />
                          <Tooltip contentStyle={{ fontSize:'9px' }} />
                          <Area yAxisId="r"  type="monotone" dataKey="ratio"    name={e("회생 가용 비율 (−)","회생 가용 비율 (−)")} stroke="#34d399" fill="#34d39920" strokeWidth={2} />
                          <Area yAxisId="kw" type="monotone" dataKey="limit_kw" name={e("회생 한계 (kW)","회생 한계 (kW)")}    stroke="#f97316" fill="#f9731620" strokeWidth={1.5} strokeDasharray="5 3" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    {/* [A18] ROC / Transient — BMS 충방전 한계 밑 */}
                    <div style={{ marginTop:'10px', padding:'8px', borderRadius:'7px', border:`1px solid ${COLORS.accent}30`, background:`${COLORS.accent}08` }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:COLORS.accent, marginBottom:'6px' }}>⚡ {e("ROC / Transient 보상","ROC / Transient Compensation")}</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
                        <PI label={e("페달 변화율 제한 (Jerk)","Pedal Rate Limit")} value={params.drv_max_jerk||3} onChange={v=>up("drv_max_jerk",v)} unit="g/s" step={0.5} min={0.5} max={20} />
                        <PI label={e("페달 1차 필터 α","Pedal Filter α")} value={params.drv_pedal_lag||0.12} onChange={v=>up("drv_pedal_lag",v)} unit="" step={0.01} min={0.01} max={0.5} />
                        <PI label={e("런치컨트롤 토크 램프율","LC Torque Ramp")} value={params.lc_torque_ramp_rate||500} onChange={v=>up("lc_torque_ramp_rate",v)} unit="Nm/s" step={50} min={50} max={5000} />
                        <PI label={e("회생 Jerk 제한","Regen Jerk Limit")} value={params.regen_max_jerk||3} onChange={v=>up("regen_max_jerk",v)} unit="m/s³" step={0.5} min={0.5} max={10} />
                      </div>
                      {/* [D-2b] 런치 컨트롤 enable + slip target */}
                      <div style={{ marginTop:'8px', padding:"6px 10px", borderRadius:'6px',
                        background:"#f9731608", border:"1px solid #f9731640",
                        display:"flex", alignItems:"center", gap:'10px', flexWrap:"wrap" }}>
                        <span style={{ fontSize:'9px', fontWeight:700, color:"#f97316" }}>🚀 Launch Control</span>
                        <button onClick={()=>up("launch_control_enable",!(params.launch_control_enable??false))}
                          style={{ padding:"2px 12px", borderRadius:'4px', border:"none", cursor:"pointer",
                            fontSize:'9px', fontWeight:700,
                            background:(params.launch_control_enable??false)?"#f97316":COLORS.border,
                            color:(params.launch_control_enable??false)?"#fff":COLORS.textMuted }}>
                          {(params.launch_control_enable??false)?"ON":"OFF"}
                        </button>
                        <div style={{ display:"flex", alignItems:"center", gap:'6px' }}>
                          <span style={{ fontSize:8.5, color:COLORS.textMuted }}>{e("목표 슬립률","Target Slip λ")}:</span>
                          <input type="range" min={0.05} max={0.25} step={0.01}
                            value={params.lc_slip_target??0.12}
                            onChange={e2=>up("lc_slip_target",parseFloat(e2.target.value))}
                            style={{ width:80, accentColor:"#f97316" }} />
                          <span style={{ fontSize:8.5, fontFamily:"monospace", color:"#f97316", fontWeight:700 }}>
                            λ={( params.lc_slip_target??0.12).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
                      </div>
                    </div>
                  </div>

                  {/* 우: 모터/인버터/배터리 온도 디레이트 */}
                  <div style={{ padding:'10px', borderRadius:'8px', border:`1px solid ${COLORS.thermalOrange}30`, background:`${COLORS.thermalOrange}08` }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:COLORS.thermalOrange, marginBottom:'8px' }}>
                      🌡️ {e("모터 디레이트","Motor Derate")}
                      <span style={{ fontWeight:400, fontSize:'9px', color:COLORS.textMuted, marginLeft:'6px' }}>{e("/ 인버터 / 배터리","/ Inv / Batt Range")}</span>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px", marginBottom:'8px' }}>
                      <PI label={e("모터 시작 °C","Motor Start °C")} value={params.T_motor_derate_start||140} onChange={v=>up("T_motor_derate_start",v)} unit="°C" step={5} min={80} max={180} />
                      <PI label={e("모터 종료 °C","Motor End °C")}   value={params.T_motor_derate_end||160}   onChange={v=>up("T_motor_derate_end",v)}   unit="°C" step={5} min={100} max={200} />
                      <PI label={e("인버터 시작 °C","Inv Start °C")} value={params.T_inv_derate_start||95}  onChange={v=>up("T_inv_derate_start",v)}  unit="°C" step={5} min={60} max={130} />
                      <PI label={e("인버터 종료 °C","Inv End °C")}   value={params.T_inv_derate_end||110}    onChange={v=>up("T_inv_derate_end",v)}    unit="°C" step={5} min={80} max={150} />
                      <PI label={e("배터리 시작 °C","Batt Start °C")} value={params.T_batt_derate_start||45} onChange={v=>up("T_batt_derate_start",v)} unit="°C" step={2} min={30} max={60} />
                      <PI label={e("배터리 종료 °C","Batt End °C")}   value={params.T_batt_derate_end||55}   onChange={v=>up("T_batt_derate_end",v)}   unit="°C" step={2} min={40} max={80} />
                    </div>
                    <div style={{ fontSize:'9px', fontWeight:700, color:COLORS.thermalOrange, marginBottom:'3px' }}>{e("온도 디레이트 커브","Thermal Derate Curve")}</div>
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart data={derateData} margin={{ top:2, right:8, bottom:2, left:0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                        <XAxis dataKey="T" tick={{ fontSize:'8px' }} label={{ value:"°C", position:"insideBottomRight", fontSize:'8px' }} />
                        <YAxis domain={[0,1.05]} tick={{ fontSize:'8px' }} label={{ value:lang==="en"?"Output Ratio (-)":"출력 비율 (-)", angle:-90, position:"insideLeft", fontSize:'8px' }} />
                        <Tooltip contentStyle={{ fontSize:'9px' }} formatter={v=>`${(v*100).toFixed(0)}%`} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize:'8px' }} />
                        <Line type="monotone" dataKey="motor" name={e("모터 출력 비율 (−)","Motor Output Ratio (−)")} stroke="#ef4444" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="inv"   name={e("인버터 출력 비율 (−)","Inverter Output Ratio (−)")} stroke="#f59e0b" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="batt"  name={e("배터리 출력 비율 (−)","Battery Output Ratio (−)")} stroke="#38bdf8" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                    {/* [A18] TCS / ESC 슬립 제어 — 온도 디레이트 밑 */}
                    <div style={{ marginTop:'10px', padding:'8px', borderRadius:'7px', border:"1px solid #fbbf2440", background:"#fbbf2408" }}>
                      <div style={{ fontSize:'10px', fontWeight:700, color:"#fbbf24", marginBottom:'4px' }}>
                        🔒 {e("TCS / ESC 슬립 제어","TCS / ESC Slip Control")}
                      </div>
                      <div style={{ fontSize:8.5, color:COLORS.textMuted, fontFamily:"monospace", marginBottom:'6px', lineHeight:1.5 }}>
                        {e("TCS: 슬립 초과 → 토크 저감 | ESC: 요레이트 오차 → 제동·토크컷","TCS: slip → torque cut | ESC: yaw err → brake/cut")}
                      </div>
                      <div style={{ display:"flex", gap:'6px', marginBottom:'8px', flexWrap:"wrap" }}>
                        {[
                          { key:"tcs_enable", label:"TCS", color:"#4ade80" },
                          { key:"esc_enable", label:"ESC", color:"#fbbf24" },
                          { key:"abs_enable", label:"ABS", color:"#60a5fa" },
                        ].map(opt=>(
                          <div key={opt.key} style={{ display:"flex", alignItems:"center", gap:'4px' }}>
                            <button onClick={()=>up(opt.key,!params[opt.key])}
                              style={{ padding:"3px 12px", borderRadius:'4px', cursor:"pointer", fontWeight:700, fontSize:'9px',
                                background: params[opt.key] ? opt.color : COLORS.bg,
                                color: params[opt.key] ? "#000" : COLORS.textDim,
                                border:`1px solid ${params[opt.key] ? opt.color : COLORS.border}` }}>
                              {params[opt.key] ? "ON ✓" : "OFF"}
                            </button>
                            <span style={{ fontSize:'9px', color:COLORS.textDim }}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 12px" }}>
                        <PI label={e("TCS 슬립 임계","TCS Slip Thr.")} value={params.tcs_slip_threshold||0.18} onChange={v=>up("tcs_slip_threshold",v)} unit="" step={0.01} min={0.05} max={0.4} />
                        <PI label={e("TCS 토크 저감","TCS Torq. Red.")} value={params.tcs_torque_reduction||0.7} onChange={v=>up("tcs_torque_reduction",v)} unit="" step={0.05} min={0.3} max={1.0} />
                        <PI label={e("ESC 요레이트 임계","ESC Yaw Thr.")} value={params.esc_yaw_threshold||0.05} onChange={v=>up("esc_yaw_threshold",v)} unit="rad/s" step={0.01} min={0.01} max={0.3} />
                        <PI label={e("ESC 제동 게인","ESC Brake Gain")} value={params.esc_brake_gain||800} onChange={v=>up("esc_brake_gain",v)} unit="Nm/(r/s)" step={50} min={100} max={3000} />
                        <PI label={e("ESC 토크컷 계수","ESC Torq.Cut")} value={params.esc_torque_cut_gain||0.3} onChange={v=>up("esc_torque_cut_gain",v)} unit="" step={0.05} min={0} max={1} />
                        {/* [D-2] ABS 파라미터 추가 */}
                        <PI label={e("ABS 목표 슬립","ABS Target Slip λ")} value={params.abs_lambda_target??0.10} onChange={v=>up("abs_lambda_target",v)} unit="" step={0.01} min={0.05} max={0.25}
                          desc={e("0.10~0.15: 일반 도로, 0.05~0.10: 눈길","0.10~0.15: dry, 0.05~0.10: snow")} />
                        <PI label={e("ABS 비례 게인","ABS Kp")} value={params.abs_Kp??2000} onChange={v=>up("abs_Kp",v)} unit="N/(slip)" step={100} min={100} max={10000}
                          desc={e("ABS 슬립 오차 → 제동력 저감 게인","Slip error → brake force reduction gain")} />
                      </div>
                    </div>
                  </div>
                </div>
                {/* [A18] 회생 맵 통합 — 진단/보정에서 이동 */}
                <div style={{ marginTop:'12px', padding:'10px', borderRadius:'8px', border:`1px solid ${COLORS.accent3}40`, background:`${COLORS.accent3}06` }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:COLORS.accent3, marginBottom:'8px' }}>
                    ♻️ {e("회생 맵 — LUT 캘리브 & 모드별 회생 강도","Regen Map — LUT Calib & Per-Mode Strength")}
                    <span style={{ marginLeft:'8px', fontSize:'9px', color:"#4ade80" }}>✅ {e("시뮬 반영","Sim")}</span>
                  </div>
                  {/* [A19] Row 1: LUT 캘리브레이션 */}
                  <div style={{ marginBottom:'12px', padding:"8px 10px", borderRadius:'7px', background:COLORS.bg, border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:"#06b6d4", marginBottom:'6px' }}>
                      📐 {e("① LUT 캘리브레이션","① LUT Calibration")}
                    </div>
                    <div style={{ fontSize:'9px', color:COLORS.textDim, fontFamily:mono, marginBottom:'8px', lineHeight:1.5 }}>
                      coast: lerp1D(v_pts, str_pts, v) × mode_strength &nbsp;|&nbsp;
                      brake: bilinear2D(lut[BPP%][v]) × mode_strength
                    </div>
                    <div style={{ display:"flex", gap:'4px', marginBottom:'8px' }}>
                      {[
                        {id:"coast",    l:e("🚗 코스팅","🚗 Coasting"),  c:"#34d399", enabled:true},
                        {id:"brake",    l:e("🛑 제동 BPP","🛑 Braking"), c:"#f87171", enabled:true},
                        {id:"onepedal", l:e("🦶 원페달","🦶 One-Pedal"), c:"#a855f7", enabled:!!params.one_pedal_enable},
                      ].map(v => (
                        <button key={v.id} onClick={() => v.enabled && setRegenView(v.id)}
                          style={{ flex:1, padding:"6px 4px", borderRadius:'5px', cursor:v.enabled?"pointer":"not-allowed", fontSize:'10px', fontWeight:700,
                            opacity:v.enabled?1:0.38,
                            background:regenView===v.id&&v.enabled?`${v.c}25`:"transparent",
                            color:regenView===v.id&&v.enabled?v.c:COLORS.textDim,
                            border:`1px solid ${regenView===v.id&&v.enabled?v.c:COLORS.border}` }}>
                          {v.l}
                        </button>
                      ))}
                    </div>
                    {regenView === "coast" && (
                      <div>
                        <div style={{ overflowX:"auto", marginBottom:'8px' }}>
                          <table style={{ borderCollapse:"collapse", fontSize:'9px', fontFamily:mono }}>
                            <thead><tr>
                              <th style={{ padding:"3px 8px", background:"#34d39922", color:"#34d399", border:`1px solid ${COLORS.border}` }}>{e("차속","Speed")}</th>
                              {coastPts.map(v => <th key={v} style={{ padding:"3px 6px", border:`1px solid ${COLORS.border}`, textAlign:"center", color:COLORS.accent }}>{v}</th>)}
                            </tr></thead>
                            <tbody><tr>
                              <td style={{ padding:"3px 8px", background:"#34d39910", color:"#34d399", border:`1px solid ${COLORS.border}`, fontWeight:700 }}>LUT</td>
                              {coastStr.map((s,i) => (
                                <td key={i} style={{ padding:"1px 2px", border:`1px solid ${COLORS.border}`, textAlign:"center" }}>
                                  <input type="number" step="0.05" min="0" max="1" value={(s||0).toFixed(2)}
                                    onChange={ev => { const n=[...coastStr]; n[i]=Math.max(0,Math.min(1,parseFloat(ev.target.value)||0)); up("regen_coast_str",n); }}
                                    style={{ width:40, textAlign:"center", background:"transparent", border:"none", color:COLORS.text, fontSize:'9px', fontFamily:mono, outline:"none" }} />
                                </td>
                              ))}
                            </tr></tbody>
                          </table>
                        </div>
                        <ResponsiveContainer width="100%" height={110}><AreaChart data={coastData} margin={{top:2,right:10,bottom:2,left:0}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                          <XAxis dataKey="v" tick={{fontSize:'8px'}} label={{value:"km/h",position:"insideBottomRight",fontSize:'8px'}} />
                          <YAxis domain={[0,1.05]} tick={{fontSize:'8px'}} />
                          <Tooltip formatter={v=>[v.toFixed(2),"LUT"]} contentStyle={{fontSize:'9px'}} />
                          <Area type="monotone" dataKey="str" stroke="#34d399" fill="#34d39920" strokeWidth={2} dot={{r:3,fill:"#34d399"}} />
                        </AreaChart></ResponsiveContainer>
                      </div>
                    )}
                    {regenView === "brake" && (
                      <div>
                        <div style={{ overflowX:"auto", marginBottom:'8px' }}>
                          <table style={{ borderCollapse:"collapse", fontSize:'9px', fontFamily:mono, width:"100%" }}>
                            <thead><tr>
                              <th style={{ padding:"3px 6px", background:"#f8717122", color:"#f87171", border:`1px solid ${COLORS.border}` }}>BPP\V</th>
                              {brakeVPts.map(v => <th key={v} style={{ padding:"3px 6px", border:`1px solid ${COLORS.border}`, textAlign:"center", color:COLORS.accent }}>{v}</th>)}
                            </tr></thead>
                            <tbody>{brakeBPts.map((bpp,ri) => (
                              <tr key={bpp}>
                                <td style={{ padding:"3px 6px", background:"#f8717110", color:"#f87171", border:`1px solid ${COLORS.border}`, fontWeight:700 }}>{bpp}%</td>
                                {brakeVPts.map((v,ci) => {
                                  const val = brakeLut[ri]&&brakeLut[ri][ci]!=null?brakeLut[ri][ci]:0;
                                  const bg = `rgba(${Math.round((1-val)*60)},${Math.round(val*180)},${Math.round(val*120)},0.2)`;
                                  return (<td key={v} style={{ padding:"1px 2px", border:`1px solid ${COLORS.border}`, background:bg, textAlign:"center" }}>
                                    <input type="number" step="0.05" min="0" max="1" value={val.toFixed(2)}
                                      onChange={ev => { const n=brakeLut.map(r=>[...r]); if(!n[ri])n[ri]=[]; n[ri][ci]=Math.max(0,Math.min(1,parseFloat(ev.target.value)||0)); up("regen_brake_lut",n); }}
                                      style={{ width:36, textAlign:"center", background:"transparent", border:"none", color:COLORS.text, fontSize:'9px', fontFamily:mono, outline:"none" }} />
                                  </td>);
                                })}
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                        <ResponsiveContainer width="100%" height={110}><LineChart data={brakeData} margin={{top:2,right:10,bottom:2,left:0}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                          <XAxis dataKey="bpp" tick={{fontSize:'8px'}} label={{value:"BPP%",position:"insideBottomRight",fontSize:'8px'}} />
                          <YAxis domain={[0,1.05]} tick={{fontSize:'8px'}} />
                          <Tooltip contentStyle={{fontSize:'9px'}} />
                          <Legend iconSize={8} wrapperStyle={{fontSize:'8px'}} />
                          {brakeVPts.map((v,ci) => <Line key={v} type="monotone" dataKey={`V${v}`} name={`${v} (km/h)`} stroke={brakeLColors[ci]} strokeWidth={1.5} dot={false} />)}
                        </LineChart></ResponsiveContainer>
                      </div>
                    )}
                    {regenView === "onepedal" && (
                      <div>
                        {!params.one_pedal_enable && (
                          <div style={{ marginBottom:'8px', padding:"6px 10px", borderRadius:'5px', background:"#f59e0b12", border:"1px solid #f59e0b40", fontSize:'9px', color:'#f59e0b' }}>
                            ⚠ {e("원페달 모드 비활성 — 이 LUT는 사용되지 않습니다","One-pedal OFF — LUT unused")}
                          </div>
                        )}
                        <div style={{ overflowX:"auto", marginBottom:'8px' }}>
                          <table style={{ borderCollapse:"collapse", fontSize:'9px', fontFamily:mono }}>
                            <thead><tr>
                              <th style={{ padding:"3px 8px", background:"#a855f722", color:"#a855f7", border:`1px solid ${COLORS.border}` }}>APP%</th>
                              {opAppPts.map(a => <th key={a} style={{ padding:"3px 6px", border:`1px solid ${COLORS.border}`, textAlign:"center", color:COLORS.accent }}>{a}%</th>)}
                            </tr></thead>
                            <tbody><tr>
                              <td style={{ padding:"3px 8px", background:"#a855f710", color:"#a855f7", border:`1px solid ${COLORS.border}`, fontWeight:700 }}>LUT</td>
                              {opStr.map((s,i) => (
                                <td key={i} style={{ padding:"1px 2px", border:`1px solid ${COLORS.border}`, textAlign:"center" }}>
                                  <input type="number" step="0.05" min="0" max="1" value={(s||0).toFixed(2)}
                                    onChange={ev => { const n=[...opStr]; n[i]=Math.max(0,Math.min(1,parseFloat(ev.target.value)||0)); up("regen_onepedal_str",n); }}
                                    style={{ width:40, textAlign:"center", background:"transparent", border:"none", color:COLORS.text, fontSize:'9px', fontFamily:mono, outline:"none" }} />
                                </td>
                              ))}
                            </tr></tbody>
                          </table>
                        </div>
                        <ResponsiveContainer width="100%" height={110}><AreaChart data={opData} margin={{top:2,right:10,bottom:2,left:0}}>
                          <CartesianGrid strokeDasharray="3 3" stroke={`${COLORS.border}60`} />
                          <XAxis dataKey="app" tick={{fontSize:'8px'}} label={{value:"APP%",position:"insideBottomRight",fontSize:'8px'}} />
                          <YAxis domain={[0,1.05]} tick={{fontSize:'8px'}} />
                          <Tooltip formatter={v=>[v.toFixed(2),"LUT"]} contentStyle={{fontSize:'9px'}} />
                          <Area type="monotone" dataKey="str" stroke="#a855f7" fill="#a855f720" strokeWidth={2} dot={{r:3,fill:"#a855f7"}} />
                        </AreaChart></ResponsiveContainer>
                      </div>
                    )}
                  </div>
                  {/* [A19] Row 2: 모드별 회생 강도 */}
                  <div style={{ padding:"8px 10px", borderRadius:'7px', background:COLORS.bg, border:`1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:COLORS.accent3, marginBottom:'6px' }}>
                      🎚️ {e("② 모드별 회생 강도","② Per-Mode Regen Strength")}
                    </div>
                    <div style={{ display:"flex", gap:'4px', marginBottom:'8px' }}>
                      {REGEN_MODES_ARB.map(m => (
                        <button key={m.id} onClick={() => setSelRegenMode(m.id)}
                          style={{ flex:1, padding:"6px 4px", borderRadius:'5px', cursor:"pointer", fontSize:'10px', fontWeight:700,
                            background: selRegenMode===m.id ? m.color : "transparent",
                            color: selRegenMode===m.id ? "#000" : COLORS.textDim,
                            border:`1px solid ${selRegenMode===m.id ? m.color : COLORS.border}` }}>
                          {m.id}
                        </button>
                      ))}
                    </div>
                    <div style={{ padding:"8px 10px", borderRadius:'6px', background:`${selRmArb.color}08`, border:`1px solid ${selRmArb.color}40` }}>
                      <PI label={`${selRmArb.id}  ${selRmArb.hint}  (×${curValArb.toFixed(2)})`}
                        value={curValArb} onChange={v=>up(selRmArb.key,v)} unit="" step={0.05} min={selRmArb.min} max={selRmArb.max} />
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
                        <PI label={e("저속 페이드 시작","Low-speed Fade")} value={params.regen_low_speed_fade_kph} onChange={v=>up("regen_low_speed_fade_kph",v)} unit="km/h" step={1} min={3} max={30} />
                        <PI label={e("최대 회생 감속도","Max Regen Decel")} value={params.regen_max_decel_ms2} onChange={v=>up("regen_max_decel_ms2",v)} unit="m/s²" step={0.1} min={0.5} max={5.0} />
                        <PI label={e("고속 블렌딩 시작","Hi-speed Blend")} value={params.regen_blend_start_kph} onChange={v=>up("regen_blend_start_kph",v)} unit="km/h" step={5} min={40} max={130} />
                        <PI label={e("고속 회생 비율","Hi-speed Ratio")} value={params.regen_blend_ratio_high} onChange={v=>up("regen_blend_ratio_high",v)} unit="" step={0.05} min={0.3} max={1.0} />
                      </div>
                      <div style={{ marginTop:'8px', display:"flex", justifyContent:"flex-end" }}>
                        <button onClick={() => { up(selRmArb.key,selRmArb.def); up("regen_max_decel_ms2",selRmArb.resetDecel); up("regen_low_speed_fade_kph",selRmArb.resetFade); up("regen_blend_start_kph",selRmArb.resetBlend*100); up("regen_blend_ratio_high",selRmArb.resetHi); }}
                          style={{ padding:"4px 14px", borderRadius:'5px', fontSize:'9px', cursor:"pointer", fontWeight:700,
                            background:`${selRmArb.color}20`, color:selRmArb.color, border:`1px solid ${selRmArb.color}50` }}>
                          🔄 {selRmArb.id} {e("초기화","Reset")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── 회생 맵 [A18: Arbitration에 통합] ── */}


          {/* ── 타이어 횡력 & 결합 슬립 ── */}
    </div>
  );
}